import {
  ICloseProposalInfo,
  IProposalDrawerValues,
  IProposalLovs
} from '@common/drawers';
import {
  AccountingTransferForm,
  AccountingTransferLookups,
  AmendmentForm,
  AmendmentLookups,
  DeactivateProviderFormFormik,
  ExistingProviderLookups,
  IDefaultCommissionInfo,
  IDefaultCommissionLovs,
  IExportBusinessPartnersFilters,
  IExportCurrencyDrawerFilters,
  IExportProposalsFilters,
  IExportTaxationFilters,
  IReinsurerValues,
  ModifyPlanSpecificTaxationFormValues,
  ModifyProviderFormFormik,
  ModifyProviderLookups,
  ModifyTaxationDrawerFormValues,
  NewPlanSpecificTaxationFormValues,
  NewProviderLookups,
  ProviderFormFormik
} from '@common/types';
import {
  DynamicFormInputType,
  FormInputTypes
} from '@components/_old/dynamicForm';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import {
  FormFieldType,
  IDateFormFieldProps
} from '@components/dynamic-form/types/inputTypes';
import { USER_ROLES } from '@core/redux/user/types';
import DateService from '@core/services/DateService';
import { IAbstractRecord } from '@models';
import { trimTrailingZeros } from '@utils/formatting-utils';
import { isEmpty, REGEX } from '@utils/validationUtils';

import dayjs from 'dayjs';
import { FormikProps } from 'formik';

export const personDetailsFormInitialValues = {
  userId: '',
  personId: '',
  type: '',
  companyName: '',
  title: '',
  titleId: '',
  firstName: '',
  middleName: '',
  lastName: '',
  maidenName: '',
  initials: '',
  dateOfBirth: '',
  nationalityCode: '',
  email: '',
  phoneType: '',
  phoneNumber: '',
  relativeOfCode: '',
  smoker: 'NO',
  weight: 0,
  height: 0,
  dateOfDeath: '',
  dateOfDeathNotified: '',
  taxable: false,
  financialReference: '',
  occupationClass: '',
  occupationClassCode: '',
  profession: '',
  employer: '',
  dateOfEmployment: '',
  salary: 0,
  dateOfSelfCertificateReceived: '',
  dateOfSelfCertificateValidated: '',
  category: '',
  dateOfKYC: '',
  comments: '',
  managerName: '',
  relation: '',
  taxResidenceCountry: '',
  remarks: '',
  registrationPlace: '',
  registrationDate: '',
  legalRepresentativeFullName: '',
  legalRepresentativeDateOfBirth: '',
  legalRepresentativeNationality: ''
};

export const businessPartnerFormInitialValues = {
  newPerson: false,
  type: '',
  title: '',
  firstName: '',
  middleName: '',
  lastName: '',
  dateOfBirth: '',
  companyName: '',
  person: '',
  email: '',
  mobileNumber: '',
  relatedCompany: '41',
  bpType: '',
  isCompany: false,
  isLocal: false,
  isRegistered: false,
  registerNumber: '',
  registrationExpiryDate: '',
  addressType: '',
  country: '',
  zone: '',
  city: '',
  street: '',
  building: '',
  addressDetails: '',
  addressDetailsArabic: '',
  taxAuxiliary: '',
  auxiliary: '',
  maxCreditLimitAllowedCurrency: '',
  maxCreditLimitAllowed: '',
  allowAccessToAllInsured: false
};

export const businessPartnerDrawerSections = (
  defaultValues: IAbstractRecord,
  lovs: Record<string, Record<string, string>>
): IDynamicFormWidgetSection[] => {
  const isNewPerson = defaultValues?.newPerson === true;
  const isIndividual = defaultValues?.type === 'INDIVIDUAL';
  const isCompany = defaultValues?.type === 'COMPANY';

  return [
    {
      sectionTitle: 'Person Details',
      inputs: {
        newPerson: {
          name: 'newPerson',
          title: 'New Person',
          type: FormFieldType.switch,
          value: defaultValues.newPerson,
          required: true
        },
        type: {
          name: 'type',
          title: 'Type',
          placeholder: 'Person Type',
          type: FormFieldType.select,
          selectOptions: lovs.personTypes,
          value: defaultValues.type,
          required: isNewPerson,
          hidden: !isNewPerson
        },
        title: {
          name: 'title',
          title: 'Title',
          placeholder: 'Title',
          type: FormFieldType.select,
          selectOptions: lovs.personTitles,
          value: defaultValues.title,
          hidden: !(isNewPerson && isIndividual),
          required: isNewPerson && isIndividual
        },
        firstName: {
          name: 'firstName',
          title: 'First Name',
          placeholder: 'First Name',
          type: FormFieldType.text,
          value: defaultValues.firstName,
          hidden: !(isNewPerson && isIndividual),
          required: isNewPerson && isIndividual
        },
        middleName: {
          name: 'middleName',
          title: 'Middle Name',
          placeholder: 'Middle Name',
          type: FormFieldType.text,
          value: defaultValues.middleName,
          hidden: !(isNewPerson && isIndividual),
          required: isNewPerson && isIndividual
        },
        lastName: {
          name: 'lastName',
          title: 'Last Name',
          placeholder: 'Last Name',
          type: FormFieldType.text,
          value: defaultValues.lastName,
          hidden: !(isNewPerson && isIndividual),
          required: isNewPerson && isIndividual
        },
        dateOfBirth: {
          name: 'dateOfBirth',
          title: 'Date of Birth',
          placeholder: 'Date of Birth',
          type: FormFieldType.date,
          value: defaultValues.dateOfBirth,
          hidden: !(isNewPerson && isIndividual),
          required: isNewPerson && isIndividual,
          maxDate: new Date()
        },
        companyName: {
          name: 'companyName',
          title: 'Company Name',
          placeholder: 'Company Name',
          type: FormFieldType.text,
          value: defaultValues.companyName,
          hidden: !(isNewPerson && isCompany),
          required: isNewPerson && isCompany
        },
        person: {
          name: 'person',
          title: 'Person',
          placeholder: 'Person',
          type: FormFieldType.select,
          selectOptions: lovs.activePersons,
          value: defaultValues.person,
          hidden: isNewPerson,
          required: !isNewPerson
        },
        email: {
          name: 'email',
          title: 'Email',
          placeholder: 'Email',
          type: FormFieldType.text,
          value: defaultValues.email,
          disabled: !defaultValues.newPerson,
          required: true,
          customValidator: ({ email }) => {
            return email && !REGEX.email.test(email) ? 'Invalid Email' : '';
          }
        },
        mobileNumber: {
          name: 'mobileNumber',
          title: 'Mobile Number',
          type: FormFieldType.phoneNumber,
          value: defaultValues.mobileNumber,
          disabled: !defaultValues.newPerson,
          required: true
        }
      }
    },
    {
      sectionTitle: 'Business Partner Details',
      inputs: {
        relatedCompany: {
          name: 'relatedCompany',
          title: 'Related Company',
          placeholder: 'Related Company',
          type: FormFieldType.select,
          selectOptions: lovs.relatedCompanies,
          value: defaultValues.relatedCompany,
          required: false,
          hidden: true
        },
        bpType: {
          name: 'bpType',
          title: 'Type',
          placeholder: 'Type',
          type: FormFieldType.select,
          selectOptions: lovs.businessPartnerTypes,
          value: defaultValues.bpType,
          required: true
        },
        isCompany: {
          name: 'isCompany',
          title: 'Is Company',
          type: FormFieldType.switch,
          value: defaultValues.isCompany,
          required: true
        },
        isLocal: {
          name: 'isLocal',
          title: 'Is Local',
          type: FormFieldType.switch,
          value: defaultValues.isLocal,
          required: true
        },
        isRegistered: {
          name: 'isRegistered',
          title: 'Is Registered',
          type: FormFieldType.switch,
          value: defaultValues.isRegistered,
          required: true
        },
        registerNumber: {
          name: 'registerNumber',
          title: 'Registration Number',
          placeholder: 'Registration Number',
          type: FormFieldType.text,
          value: defaultValues.registerNumber
        },
        registrationExpiryDate: {
          name: 'registrationExpiryDate',
          title: 'Registration Expiry Date',
          type: FormFieldType.date,
          value: defaultValues.registrationExpiryDate
        }
      }
    },
    {
      sectionTitle: 'Primary Address Details',
      hidden: !isNewPerson,
      inputs: {
        addressType: {
          name: 'addressType',
          title: 'Address Type',
          placeholder: 'Select Address Type',
          type: FormFieldType.select,
          selectOptions: lovs.addressTypes,
          value: defaultValues.addressType,
          required: isNewPerson
        },
        country: {
          name: 'country',
          title: 'Country',
          placeholder: 'Country',
          type: FormFieldType.select,
          selectOptions: lovs.countries,
          value: defaultValues.country,
          required: isNewPerson
        },
        zone: {
          name: 'zone',
          title: 'Zone',
          placeholder: 'Zone',
          type: FormFieldType.select,
          selectOptions:
            defaultValues.country === 'LB' && lovs['zones']
              ? lovs['zones']
              : {},
          value: defaultValues.zone
        },
        city: {
          name: 'city',
          title: 'City',
          placeholder: 'City',
          type: FormFieldType.text,
          value: defaultValues.city,
          required: isNewPerson
        },
        street: {
          name: 'street',
          title: 'Street',
          placeholder: 'Street',
          type: FormFieldType.text,
          value: defaultValues.street
        },
        building: {
          name: 'building',
          title: 'Building',
          placeholder: 'Building',
          type: FormFieldType.text,
          value: defaultValues.building
        },
        addressDetails: {
          name: 'addressDetails',
          title: 'Address Details',
          placeholder: 'Address Details',
          type: FormFieldType.text,
          value: defaultValues.addressDetails
        },
        addressDetailsArabic: {
          name: 'addressDetailsArabic',
          title: 'Address Details (Arabic)',
          placeholder: 'Address Details In Arabic',
          type: FormFieldType.text,
          value: defaultValues.addressDetailsArabic
        }
      }
    },
    {
      sectionTitle: 'Accounting Details',
      inputs: {
        taxAuxiliary: {
          name: 'taxAuxiliary',
          title: 'Tax Auxiliary',
          placeholder: 'Tax Auxiliary',
          type: FormFieldType.text,
          value: defaultValues.taxAuxiliary,
          required: true,
          customValidator: ({ taxAuxiliary }) => {
            return taxAuxiliary?.length != 8
              ? 'Tax Auxiliary should be 8 characters'
              : '';
          }
        },
        auxiliary: {
          name: 'auxiliary',
          title: 'Auxiliary',
          placeholder: 'Auxiliary',
          type: FormFieldType.text,
          value: defaultValues.auxiliary,
          required: true,
          customValidator: ({ auxiliary }) => {
            return auxiliary?.length != 8
              ? 'Auxiliary should be 8 characters'
              : '';
          }
        }
      }
    },
    {
      sectionTitle: 'Credit Limit Details',
      inputs: {
        maxCreditLimitAllowedCurrency: {
          name: 'maxCreditLimitAllowedCurrency',
          title: 'Max Credit Limit Allowed (Currency)',
          placeholder: 'Currency',
          type: FormFieldType.select,
          selectOptions: lovs.currencies,
          value: defaultValues.maxCreditLimitAllowedCurrency,
          required: true
        },
        maxCreditLimitAllowed: {
          name: 'maxCreditLimitAllowed',
          title: 'Max Credit Limit Allowed',
          placeholder: 'Max Credit Limit Allowed',
          type: FormFieldType.currency,
          maxDecimalPrecision: 0,
          value: defaultValues.maxCreditLimitAllowed,
          required: true
        }
      }
    },
    {
      sectionTitle: 'Additional Details',
      inputs: {
        allowAccessToAllInsured: {
          name: 'allowAccessToAllInsured',
          title: 'Allow Access to All Insureds',
          type: FormFieldType.switch,
          value: defaultValues.allowAccessToAllInsured,
          required: true
        }
      }
    }
  ];
};

export const providerDrawerSections = (
  providerFormik: FormikProps<ProviderFormFormik>,
  lovs: NewProviderLookups | ExistingProviderLookups
): IDynamicFormWidgetSection[] => {
  const isNewPerson = providerFormik?.values?.newPerson === true;

  return [
    {
      sectionTitle: 'Person Details',
      inputs: {
        newPerson: {
          name: 'newPerson',
          title: 'New Person',
          type: FormFieldType.switch,
          value: providerFormik?.values?.newPerson,
          required: true
        },
        title: {
          name: 'title',
          title: 'Title',
          placeholder: 'Title',
          type: FormFieldType.select,
          selectOptions: (lovs as NewProviderLookups)?.titles,
          value: providerFormik?.values?.title,
          hidden: !isNewPerson,
          required: isNewPerson
        },
        firstName: {
          name: 'firstName',
          title: 'First Name',
          placeholder: 'First Name',
          type: FormFieldType.text,
          value: providerFormik?.values?.firstName,
          hidden: !isNewPerson,
          required: isNewPerson
        },
        middleName: {
          name: 'middleName',
          title: 'Middle Name',
          placeholder: 'Middle Name',
          type: FormFieldType.text,
          value: providerFormik?.values?.middleName,
          hidden: !isNewPerson,
          required: isNewPerson
        },
        lastName: {
          name: 'lastName',
          title: 'Last Name',
          placeholder: 'Last Name',
          type: FormFieldType.text,
          value: providerFormik?.values?.lastName,
          hidden: !isNewPerson,
          required: isNewPerson
        },
        dateOfBirth: {
          name: 'dateOfBirth',
          title: 'Date of Birth',
          placeholder: 'Date of Birth',
          type: FormFieldType.date,
          value: providerFormik?.values?.dateOfBirth,
          hidden: !isNewPerson,
          required: isNewPerson,
          maxDate: new Date()
        },
        person: {
          name: 'personId',
          title: 'Person',
          placeholder: 'Person',
          type: FormFieldType.select,
          selectOptions: (lovs as ExistingProviderLookups)?.persons,
          value: providerFormik?.values?.personId,
          hidden: isNewPerson,
          required: !isNewPerson
        },
        email: {
          name: 'email',
          title: 'Email',
          placeholder: 'Email',
          type: FormFieldType.text,
          value: providerFormik?.values?.email,
          required: isNewPerson,
          disabled: !isNewPerson,
          customValidator: ({ email }) => {
            return email && !REGEX.email.test(email) ? 'Invalid Email' : '';
          }
        },
        phoneType: {
          name: 'phoneType',
          title: 'Phone Type',
          type: FormFieldType.select,
          placeholder: 'Phone Type',
          selectOptions: (lovs as NewProviderLookups)?.phoneType,
          value: providerFormik?.values?.phoneType,
          required: isNewPerson,
          disabled: !isNewPerson
        },
        phoneNumber: {
          name: 'phoneNumber',
          title: 'Phone Number',
          type: FormFieldType.phoneNumber,
          placeholder: 'Phone Number',
          value: providerFormik?.values?.phoneNumber,
          required: isNewPerson,
          disabled: !isNewPerson
        },
        isTaxable: {
          name: 'isTaxable',
          title: 'Taxable',
          type: FormFieldType.switch,
          value: providerFormik?.values?.isTaxable,
          required: isNewPerson,
          disabled: !isNewPerson
        }
      }
    },
    {
      sectionTitle: 'Primary Address Details',
      hidden: !isNewPerson,
      inputs: {
        addressType: {
          name: 'addressType',
          title: 'Address Type',
          placeholder: 'Select Address Type',
          type: FormFieldType.select,
          selectOptions: (lovs as NewProviderLookups)?.addressType,
          value: providerFormik?.values?.addressType,
          required: isNewPerson
        },
        country: {
          name: 'address.country',
          title: 'Country',
          placeholder: 'Country',
          type: FormFieldType.select,
          selectOptions: (lovs as NewProviderLookups)?.countries,
          value: providerFormik?.values?.address?.country,
          required: isNewPerson
        },
        zone: {
          name: 'address.zone',
          title: 'Zone',
          placeholder: 'Zone',
          type: FormFieldType.select,
          selectOptions:
            providerFormik?.values?.address?.country === 'LB' &&
            (lovs as NewProviderLookups)?.zones
              ? (lovs as NewProviderLookups)?.zones
              : {},
          value: providerFormik?.values?.address?.zone,
          hidden: providerFormik?.values?.address?.country !== 'LB'
        },
        city: {
          name: 'address.city',
          title: 'City',
          placeholder: 'City',
          type: FormFieldType.text,
          value: providerFormik?.values?.address?.city,
          required: isNewPerson
        },
        street: {
          name: 'address.street',
          title: 'Street',
          placeholder: 'Street',
          type: FormFieldType.text,
          value: providerFormik?.values?.address?.street
        },
        building: {
          name: 'address.building',
          title: 'Building',
          placeholder: 'Building',
          type: FormFieldType.text,
          value: providerFormik?.values?.address?.building
        },
        addressDetails: {
          name: 'address.addressDetails',
          title: 'Address Details',
          placeholder: 'Address Details',
          type: FormFieldType.text,
          value: providerFormik?.values?.address?.addressDetails
        },
        addressDetailsArabic: {
          name: 'address.addressDetailsArabic',
          title: 'Address Details (Arabic)',
          placeholder: 'Address Details In Arabic',
          type: FormFieldType.text,
          value: providerFormik?.values?.address?.addressDetailsArabic
        }
      }
    },
    {
      sectionTitle: 'Provider Details',
      inputs: {
        type: {
          name: 'type',
          title: 'Type',
          placeholder: 'Type',
          type: FormFieldType.select,
          selectOptions: lovs?.providerTypes,
          value: providerFormik?.values?.type,
          required: true
        },
        isCompany: {
          name: 'isCompany',
          title: 'Company',
          type: FormFieldType.switch,
          value: providerFormik?.values?.isCompany,
          required: true
        },
        isLocal: {
          name: 'isLocal',
          title: 'Local',
          type: FormFieldType.switch,
          value: providerFormik?.values?.isLocal,
          required: true
        },
        contractStartDate: {
          name: 'contractStartDate',
          title: 'Contract Start Date',
          type: FormFieldType.date,
          value: providerFormik?.values?.contractStartDate,
          maxDate: new Date(providerFormik?.values?.contractEndDate),
          required: true
        },
        contractEndDate: {
          name: 'contractEndDate',
          title: 'Contract End Date',
          type: FormFieldType.date,
          value: providerFormik?.values?.contractEndDate,
          minDate: new Date(providerFormik?.values?.contractStartDate),
          hidden:
            !providerFormik?.values?.status ||
            providerFormik?.values?.status?.toUpperCase() === 'ACTIVE',
          required: providerFormik?.values?.status?.toUpperCase() !== 'ACTIVE'
        },
        status: {
          name: 'status',
          title: 'Status',
          type: FormFieldType.select,
          placeholder: 'Select Status',
          value: !isEmpty(providerFormik?.values?.status)
            ? providerFormik?.values?.status
            : [],
          required: true,
          selectOptions: lovs?.status
        }
      }
    }
  ];
};

export const modifyProviderDrawerSections = (
  providerFormik: FormikProps<ModifyProviderFormFormik>,
  lovs: ModifyProviderLookups
): IDynamicFormWidgetSection[] => {
  return [
    {
      sectionTitle: 'Person Details',
      inputs: {
        title: {
          name: 'title',
          title: 'Title',
          placeholder: 'Title',
          type: FormFieldType.select,
          selectOptions: (lovs as NewProviderLookups)?.titles,
          value: providerFormik?.values?.title,
          disabled: true,
          required: true
        },
        firstName: {
          name: 'firstName',
          title: 'First Name',
          placeholder: 'First Name',
          type: FormFieldType.text,
          value: providerFormik?.values?.firstName,
          disabled: true,
          required: true
        },
        middleName: {
          name: 'middleName',
          title: 'Middle Name',
          placeholder: 'Middle Name',
          type: FormFieldType.text,
          value: providerFormik?.values?.middleName,
          disabled: true,
          required: true
        },
        lastName: {
          name: 'lastName',
          title: 'Last Name',
          placeholder: 'Last Name',
          type: FormFieldType.text,
          value: providerFormik?.values?.lastName,
          disabled: true,
          required: true
        },
        dateOfBirth: {
          name: 'dateOfBirth',
          title: 'Date of Birth',
          placeholder: 'Date of Birth',
          type: FormFieldType.date,
          value: providerFormik?.values?.dateOfBirth,
          disabled: true,
          required: true
        },
        email: {
          name: 'email',
          title: 'Email',
          placeholder: 'Email',
          type: FormFieldType.text,
          value: providerFormik?.values?.email,
          disabled: true,
          required: true,
          customValidator: ({ email }) => {
            return email && !REGEX.email.test(email) ? 'Invalid Email' : '';
          }
        },
        phoneType: {
          name: 'phoneType',
          title: 'Phone Type',
          type: FormFieldType.select,
          placeholder: 'Phone Type',
          selectOptions: (lovs as NewProviderLookups)?.phoneType,
          value: providerFormik?.values?.phoneType,
          disabled: true,
          required: true
        },
        phoneNumber: {
          name: 'phoneNumber',
          title: 'Phone Number',
          type: FormFieldType.phoneNumber,
          placeholder: 'Phone Number',
          value: providerFormik?.values?.phoneNumber,
          disabled: true,
          required: true
        },
        isTaxable: {
          name: 'isTaxable',
          title: 'Taxable',
          type: FormFieldType.switch,
          value: providerFormik?.values?.isTaxable,
          disabled: true
        }
      }
    },
    {
      sectionTitle: 'Provider Details',
      inputs: {
        type: {
          name: 'type',
          title: 'Type',
          placeholder: 'Type',
          type: FormFieldType.select,
          selectOptions: lovs?.providerTypes,
          value: providerFormik?.values?.type,
          disabled: true,
          required: true
        },
        isCompany: {
          name: 'isCompany',
          title: 'Company',
          type: FormFieldType.switch,
          value: providerFormik?.values?.isCompany
        },
        isLocal: {
          name: 'isLocal',
          title: 'Local',
          type: FormFieldType.switch,
          value: providerFormik?.values?.isLocal
        },
        contractStartDate: {
          name: 'contractStartDate',
          title: 'Contract Start Date',
          type: FormFieldType.date,
          value: providerFormik?.values?.contractStartDate,
          maxDate: providerFormik?.values?.contractEndDate
            ? new Date(providerFormik.values.contractEndDate)
            : undefined,
          required: true
        }
      }
    }
  ];
};

export const deactivateProviderDrawerSections = (
  providerFormik: FormikProps<DeactivateProviderFormFormik>
): IDynamicFormWidgetSection[] => {
  return [
    {
      sectionTitle: '',
      inputs: {
        contractEndDate: {
          name: 'contractEndDate',
          title: 'Contract End Date',
          type: FormFieldType.date,
          value: providerFormik?.values?.contractEndDate,
          minDate: new Date(providerFormik?.values?.contractStartDate),
          maxDate: new Date(),
          required: true
        }
      }
    }
  ];
};

export const initialValues = {
  rangeCode: '',
  rangeYear: '',
  rangeFrom: 0,
  rangeTo: 0,
  rangeStatus: 'ACTIVE'
};

export const vignetteRangeDrawerSections: IDynamicFormWidgetSection[] = [
  {
    inputs: {
      rangeYear: {
        name: 'rangeYear',
        title: 'Year',
        type: FormFieldType.number,
        placeholder: 'Enter Year',
        value: initialValues.rangeYear,
        required: true,
        customValidator: (values) => {
          const year = parseInt(values.rangeYear);
          if (isNaN(year) || year < 1900 || year > 2100) {
            return 'Invalid Year format';
          }

          return '';
        }
      },
      rangeCode: {
        name: 'rangeCode',
        title: 'Code',
        type: FormFieldType.text,
        placeholder: 'Enter Vignette Code',
        value: initialValues.rangeCode,
        required: true
      },
      rangeFrom: {
        name: 'rangeFrom',
        title: 'Range From',
        type: FormFieldType.number,
        placeholder: 'Enter Range From',
        value: initialValues.rangeFrom || '',
        required: true,
        maxDecimalPrecision: 0
      },
      rangeTo: {
        name: 'rangeTo',
        title: 'Range To',
        type: FormFieldType.number,
        placeholder: 'Enter Range To',
        value: initialValues.rangeTo || '',
        required: true,
        maxDecimalPrecision: 0,
        customValidator: (allValues) => {
          const min = parseFloat(allValues.rangeFrom ?? 0);
          const max = parseFloat(allValues.rangeTo ?? 0);
          if (min && max && min > max)
            return 'Range To cannot be less than Range From';

          return '';
        }
      },
      rangeStatus: {
        name: 'rangeStatus',
        title: 'Status',
        type: FormFieldType.select,
        placeholder: 'Select Status',
        value: !isEmpty(initialValues.rangeStatus)
          ? initialValues.rangeStatus
          : [],
        required: true,
        hidden: true,
        selectOptions: {}
      }
    }
  }
];

export const getModifyDrawerSections = (
  defaultValues: IAbstractRecord,
  lovs: Record<string, Record<string, string>>
): IDynamicFormWidgetSection[] => {
  const isIndividual = defaultValues?.type === 'INDIVIDUAL';
  const isCompany = defaultValues?.type === 'COMPANY';

  return [
    {
      sectionTitle: 'Person Details',
      inputs: {
        title: {
          name: 'title',
          title: 'Title',
          type: FormFieldType.select,
          selectOptions: lovs.personTitles,
          value: defaultValues?.title,
          hidden: !isIndividual,
          required: isIndividual,
          disabled: true
        },
        firstName: {
          name: 'firstName',
          title: 'First Name',
          type: FormFieldType.text,
          value: defaultValues?.firstName,
          hidden: !isIndividual,
          required: isIndividual,
          disabled: true
        },
        middleName: {
          name: 'middleName',
          title: 'Middle Name',
          type: FormFieldType.text,
          value: defaultValues?.middleName,
          hidden: !isIndividual,
          required: isIndividual,
          disabled: true
        },
        lastName: {
          name: 'lastName',
          title: 'Last Name',
          type: FormFieldType.text,
          value: defaultValues?.lastName,
          hidden: !isIndividual,
          required: isIndividual,
          disabled: true
        },
        dateOfBirth: {
          name: 'dateOfBirth',
          title: 'Date Of Birth',
          type: FormFieldType.date,
          value: defaultValues?.dateOfBirth,
          maxDate: new Date(),
          hidden: !isIndividual,
          required: isIndividual,
          disabled: true
        },
        companyName: {
          name: 'companyName',
          title: 'Company Name',
          type: FormFieldType.text,
          value: defaultValues?.companyName,
          hidden: !isCompany,
          required: isCompany,
          disabled: true
        },
        email: {
          name: 'email',
          title: 'Email',
          type: FormFieldType.text,
          value: defaultValues?.email,
          required: true,
          customValidator: ({ email }) =>
            email && !REGEX.email.test(email) ? 'Invalid Email' : '',
          disabled: true
        },
        mobileNumber: {
          name: 'mobileNumber',
          title: 'Mobile Number',
          type: FormFieldType.phoneNumber,
          value: defaultValues?.mobileNumber,
          required: true,
          disabled: true
        }
      }
    },
    {
      sectionTitle: 'Business Partner Details',
      inputs: {
        relatedCompany: {
          name: 'relatedCompany',
          title: 'Related Company',
          type: FormFieldType.select,
          selectOptions: lovs.relatedCompanies,
          value: defaultValues?.relatedCompany,
          required: false,
          hidden: true
        },
        bpType: {
          name: 'bpType',
          title: 'Type',
          type: FormFieldType.select,
          selectOptions: lovs.businessPartnerTypes,
          value: defaultValues?.bpType,
          required: true
        },
        isCompany: {
          name: 'isCompany',
          title: 'Is Company',
          type: FormFieldType.switch,
          value: defaultValues?.isCompany,
          required: true
        },
        isLocal: {
          name: 'isLocal',
          title: 'Is Local',
          type: FormFieldType.switch,
          value: defaultValues?.isLocal,
          required: true
        },
        isRegistered: {
          name: 'isRegistered',
          title: 'Is Registered',
          type: FormFieldType.switch,
          value: defaultValues?.isRegistered,
          required: true
        },
        registerNumber: {
          name: 'registerNumber',
          title: 'Registration Number',
          placeholder: 'Registration Number',
          type: FormFieldType.text,
          value: defaultValues?.registerNumber
        },
        registrationExpiryDate: {
          name: 'registrationExpiryDate',
          title: 'Registration Expiry Date',
          type: FormFieldType.date,
          value: defaultValues?.registrationExpiryDate
        }
      }
    },
    {
      sectionTitle: 'Accounting Details',
      inputs: {
        taxAuxiliary: {
          name: 'taxAuxiliary',
          title: 'Tax Auxiliary',
          type: FormFieldType.text,
          value: defaultValues?.taxAuxiliary,
          required: true,
          customValidator: ({ taxAuxiliary }) =>
            taxAuxiliary?.length !== 8
              ? 'Tax Auxiliary should have 8 characters'
              : ''
        },
        auxiliary: {
          name: 'auxiliary',
          title: 'Auxiliary',
          type: FormFieldType.text,
          value: defaultValues?.auxiliary,
          required: true,
          customValidator: ({ auxiliary }) =>
            auxiliary?.length !== 8 ? 'Auxiliary should have 8 characters' : ''
        }
      }
    },
    {
      sectionTitle: 'Additional Details',
      inputs: {
        allowAccessToAllInsured: {
          name: 'allowAccessToAllInsured',
          title: 'Allow Access to All Insureds',
          type: FormFieldType.switch,
          value: defaultValues?.allowAccessToAllInsured,
          required: true
        }
      }
    }
  ];
};

export const accountingTransferDrawerSections = (
  formik: FormikProps<AccountingTransferForm>,
  lovs: AccountingTransferLookups
): IDynamicFormWidgetSection[] => {
  const from = formik.values.transferFrom
    ? new Date(formik.values.transferFrom as any)
    : undefined;
  // const to = formik.values.transferTo
  //   ? new Date(formik.values.transferTo as any)
  //   : undefined;

  return [
    {
      inputs: {
        transferTypeId: {
          name: 'transferType',
          title: 'Transfer Type',
          placeholder: 'Select Transfer Type',
          type: FormFieldType.select,
          selectOptions: lovs.transferTypes,
          value: formik.values.transferType,
          required: true
        },
        transferFrom: {
          name: 'transferFrom',
          title: 'Transfer From',
          placeholder: 'Select Transfer Start Date',
          type: FormFieldType.date,
          value: formik.values.transferFrom,
          required: true
        },
        transferTo: {
          name: 'transferTo',
          title: 'Transfer To',
          placeholder: 'Select Transfer End Date',
          type: FormFieldType.date,
          value: formik.values.transferTo,
          required: true,
          minDate: from
        }
      }
    }
  ];
};

export const getUserDrawerSections = (
  businessUserId: string,
  defaultValues: Record<string, any> = initialValues,
  lookups: Record<string, Record<string, string>>,
  isNewPerson: boolean
): IDynamicFormWidgetSection[] => [
  {
    sectionTitle: 'Person Details',
    inputs: {
      newPerson: {
        name: 'newPerson',
        title: 'New Person',
        type: FormFieldType.switch,
        value: defaultValues.newPerson,
        required: true,
        disabled: false,
        hidden: !!businessUserId
      },
      title: {
        name: 'title',
        title: 'Title',
        type: FormFieldType.select,
        placeholder: 'Title',
        value: defaultValues?.title ?? defaultValues?.person?.title ?? '',
        selectOptions: lookups['Titles'],
        hidden: businessUserId ? false : !isNewPerson,
        required: isNewPerson && !businessUserId,
        disabled: !!businessUserId
      },
      firstName: {
        name: 'firstName',
        title: 'First Name',
        placeholder: 'First Name',
        type: FormFieldType.text,
        value: defaultValues?.user?.firstName,
        hidden: businessUserId ? false : !isNewPerson,
        required: businessUserId ? true : isNewPerson,
        disabled: !!businessUserId
      },
      middleName: {
        name: 'middleName',
        title: 'Middle Name',
        placeholder: 'Middle Name',
        type: FormFieldType.text,
        value: defaultValues?.user?.middleName,
        hidden: businessUserId ? false : !isNewPerson,
        required: businessUserId ? true : isNewPerson,
        disabled: !!businessUserId
      },
      lastName: {
        name: 'lastName',
        title: 'Last Name',
        placeholder: 'Last Name',
        type: FormFieldType.text,
        value: defaultValues?.user?.lastName,
        hidden: businessUserId ? false : !isNewPerson,
        required: businessUserId ? true : isNewPerson,
        disabled: !!businessUserId
      },
      dateOfBirth: {
        name: 'dateOfBirth',
        title: 'Date of Birth',
        placeholder: 'Date of Birth',
        type: FormFieldType.date,
        value: defaultValues.dateOfBirth,
        hidden: !isNewPerson,
        required: isNewPerson,
        disabled: false,
        maxDate: new Date()
      },
      person: {
        name: 'person',
        title: 'Person',
        placeholder: 'Select Person',
        type: FormFieldType.select,
        value: defaultValues.person,
        hidden: isNewPerson || !!businessUserId,
        required: !isNewPerson && !businessUserId,
        disabled: false,
        selectOptions: lookups['ActivePersons']
      },
      email: {
        name: 'email',
        title: 'Email',
        placeholder: 'Email',
        type: FormFieldType.text,
        value: defaultValues?.user?.email,
        required: true,
        disabled: !!businessUserId || !isNewPerson,
        customValidator(values) {
          const email = values?.email;
          const regex = REGEX.email;
          if (email && !regex.test(email)) {
            return 'Invalid Email';
          }

          return '';
        }
      },
      password: {
        name: 'password',
        title: 'Password',
        placeholder: 'Password',
        type: FormFieldType.password,
        value: defaultValues?.user?.password,
        strongValidation: true,
        hidden: !!businessUserId,
        required: false,
        disabled: false
      },
      mobileNumber: {
        name: 'mobileNumber',
        title: 'Mobile Number',
        placeholder: 'Mobile Number',
        type: FormFieldType.phoneNumber,
        value: defaultValues?.user?.phoneNumber || '',
        required: true,
        disabled: !!businessUserId || !isNewPerson
      }
    }
  },
  {
    sectionTitle: 'Access Details',
    inputs: {
      access: {
        name: 'access',
        title: 'Access Type',
        type: FormFieldType.select,
        placeholder: 'Access Type',
        value: defaultValues.user?.access,
        selectOptions: lookups['access'],
        required: true,
        disabled: false
      },
      role: {
        name: 'role',
        title: 'Role',
        type: FormFieldType.select,
        placeholder: 'Role',
        value:
          defaultValues?.user?.userRoles
            ?.map((userRole: any) => userRole.role?.name)
            .join(', ') || '',
        selectOptions:
          defaultValues?.user?.access === 'POS'
            ? Object.fromEntries(
                Object.entries(lookups['Roles']).filter(([key]) =>
                  (
                    [
                      USER_ROLES['POS-Normal'],
                      USER_ROLES['POS-Main']
                    ] as string[]
                  ).includes(key)
                )
              )
            : defaultValues?.user?.access === 'Backoffice'
              ? Object.fromEntries(
                  Object.entries(lookups['Roles']).filter(([key]) =>
                    ['Admin', 'Underwriter', 'Accountant'].includes(key)
                  )
                )
              : lookups['Roles'],
        required: true,
        disabled: false
      },
      type: {
        name: 'type',
        title: 'Type',
        type: FormFieldType.select,
        placeholder: 'Type',
        value: defaultValues?.user?.type,
        selectOptions: lookups['UserType'],
        required: true,
        disabled: false
      }
    }
  },

  {
    sectionTitle: 'Primary Address Details',
    hidden: !!businessUserId || !isNewPerson,
    inputs: {
      addressType: {
        name: 'addressType',
        title: 'Address Type',
        placeholder: 'Address Type',
        type: FormFieldType.select,
        value: defaultValues.addressType,
        selectOptions: lookups['PersonAddressType'],
        required: !businessUserId && isNewPerson,
        disabled: false
      },
      country: {
        name: 'country',
        title: 'Country',
        type: FormFieldType.select,
        placeholder: 'Country',
        value: defaultValues.country,
        selectOptions: lookups['countries'],
        required: !businessUserId && isNewPerson,
        disabled: false
      },
      zone: {
        name: 'zone',
        title: 'Zone',
        type: FormFieldType.select,
        placeholder: 'Zone',
        value: defaultValues.zone,
        selectOptions:
          defaultValues.country === 'LB' && lookups['zones']
            ? lookups['zones']
            : {},
        required: false,
        disabled: false
      },

      city: {
        name: 'city',
        title: 'City',
        placeholder: 'City',
        type: FormFieldType.text,
        value: defaultValues.city,
        required: !businessUserId && isNewPerson,
        disabled: false
      },
      street: {
        name: 'street',
        title: 'Street',
        placeholder: 'Street',
        type: FormFieldType.text,
        value: defaultValues.street,
        required: false,
        disabled: false
      },
      building: {
        name: 'building',
        title: 'Building',
        placeholder: 'Building',
        type: FormFieldType.text,
        value: defaultValues.street,
        required: false,
        disabled: false
      },
      addressDetails: {
        name: 'addressDetails',
        title: 'Address Details',
        placeholder: 'Address Details',
        type: FormFieldType.text,
        value: defaultValues.addressDetails,
        required: false,
        disabled: false
      },
      addressDetailsArabic: {
        name: 'addressDetailsArabic',
        title: 'Address Details Arabic',
        placeholder: 'Address Details in Arabic',
        type: FormFieldType.text,
        value: defaultValues.addressDetailsArabic,
        required: false,
        disabled: false
      }
    }
  },
  {
    sectionTitle: 'Additional Details',
    hidden: !!businessUserId,
    inputs: {
      status: {
        name: 'status',
        title: 'Status',
        type: FormFieldType.select,
        placeholder: 'Status',
        value: defaultValues.status || 'ACTIVE',
        selectOptions: lookups['UserStatus'],
        required: !businessUserId,
        disabled: false
      }
    }
  }
];

export const amendmentDrawerSections = (
  formik: FormikProps<AmendmentForm>,
  lovs: AmendmentLookups,
  policyData?: {
    policyLineCode?: string | number;
    policyEffectiveDate?: string;
    policyExpiryDate?: string;
  }
): IDynamicFormWidgetSection[] => {
  const isEndorsement = formik.values.amendmentType === 'POLICY_ENDORSEMENT';
  const isCancellation = formik.values.amendmentType === 'POLICY_CANCELLATION';

  // Line code logic
  const lineCodeNumber =
    typeof policyData?.policyLineCode === 'string'
      ? parseInt(policyData.policyLineCode, 10)
      : policyData?.policyLineCode;
  const isLinecode52 = lineCodeNumber === 52;

  // Dynamic labels based on amendment type
  const getIssueLabel = () => {
    return isEndorsement ? 'Amendment Issue Date' : 'Cancellation Issue Date';
  };

  const getEffectiveLabel = () => {
    return isEndorsement
      ? 'Amendment Effective Date'
      : 'Cancellation Effective Date';
  };

  // Field enable/disable logic
  const isAmendmentTypeEnabled = !isLinecode52;
  const isEffectiveDateEnabled = !isLinecode52 && isCancellation;

  return [
    {
      inputs: {
        amendmentType: {
          name: 'amendmentType',
          title: 'Amendment Type',
          placeholder: 'Select Amendment Type',
          type: FormFieldType.select,
          selectOptions: lovs.amendmentTypes,
          value: formik.values.amendmentType,
          required: true,
          disabled: !isAmendmentTypeEnabled
        },

        amendmentSubtypeId: {
          name: 'amendmentSubtypeId',
          title: 'Amendment Sub Type',
          placeholder: 'Select Amendment Sub Type',
          type: FormFieldType.select,
          selectOptions: lovs.amendmentSubtypes,
          value: formik.values.amendmentSubtypeId ?? '',
          hidden: !isEndorsement,
          required: isEndorsement
        },

        amendmentIssueDate: {
          name: 'amendmentIssueDate',
          title: getIssueLabel(),
          placeholder: getIssueLabel(),
          type: FormFieldType.date,
          value: formik.values.amendmentIssueDate,
          required: true,
          disabled: true
        },

        amendmentEffectiveDate: {
          name: 'amendmentEffectiveDate',
          title: getEffectiveLabel(),
          placeholder: getEffectiveLabel(),
          type: FormFieldType.date,
          value: formik.values.amendmentEffectiveDate,
          required: true,
          disabled: !isEffectiveDateEnabled,
          // Date range from policy effective to expiry date
          minDate: policyData?.policyEffectiveDate
            ? new Date(policyData.policyEffectiveDate)
            : undefined,
          maxDate: policyData?.policyExpiryDate
            ? new Date(policyData.policyExpiryDate)
            : undefined
        },

        amendmentReason: {
          name: 'amendmentReason',
          title: 'Cancellation Reason',
          placeholder: 'Enter cancellation reason',
          type: FormFieldType.textArea,
          value: formik.values.amendmentReason ?? '',
          hidden: !isCancellation,
          required: isCancellation
        }
      }
    }
  ];
};

export const userFormInitialValues = (newPerson?: boolean) => ({
  person: '',
  email: '',
  password: '',
  type: '',
  account: '',
  access: '',
  SalesforceManagement_BusinessUserStatuses: 'ACTIVE',
  personId: '',
  newPerson: newPerson || false,
  isAdmin: false,
  role: '',
  title: '',
  firstName: '',
  lastName: '',
  middleName: '',
  mobileNumber: '',
  addressType: '',
  address: {
    country: '',
    city: '',
    street: '',
    zone: '',
    building: ''
  },
  addressDetails: '',
  addressDetailsArabic: '',
  status: 'ACTIVE',
  user: {
    email: '',
    password: '',
    access: '',
    type: '',
    phoneNumber: '',
    firstName: '',
    middleName: '',
    lastName: '',
    title: ''
  }
});

// Proposal drawer constants
export const proposalDrawerInitialValues: IProposalDrawerValues = {
  businessPartnerId: '',
  line: '',
  subline: '',
  plan: '',
  policyCurrency: '',
  isGroup: false,
  isRenewal: false,
  renewedFromPolicy: '',
  policyEffectiveDate: null,
  renewWithExistingPlan: true
};

export const proposalDrawerSections = (
  data: IProposalDrawerValues = proposalDrawerInitialValues,
  lists: IProposalLovs,
  showGroupPolicy: boolean,
  renewalContext?: {
    lineCode?: string;
    isRenewal: boolean;
    policyId?: string;
    policyNumber?: string;
  },
  userAccess?: 'Backoffice' | 'POS'
): IDynamicFormWidgetSection[] => {
  const sections: IDynamicFormWidgetSection[] = [];
  // Filter line options when line/subline fields are enabled (renewal case)
  const getFilteredLineOptions = () => {
    const lineCodeFromContext = renewalContext?.lineCode;
    const lineCodeFromData =
      data.line && lists.line[data.line]
        ? lists.line[data.line].split(' -')[0]?.trim()
        : null;

    const filter =
      ['4', '50', '51'].includes(lineCodeFromContext) ||
      ['4', '50', '51'].includes(lineCodeFromData);
    if (!filter || !data.isRenewal) {
      return lists.line;
    }

    // Filter to only include lines with codes 4, 50, 51
    const filteredLines: Record<string, string> = {};
    Object.entries(lists.line || {}).forEach(([id, displayValue]) => {
      const lineCode = displayValue.split(' -')[0]?.trim();
      if (['4', '50', '51'].includes(lineCode)) {
        filteredLines[id] = displayValue;
      }
    });

    return filteredLines;
  };
  sections.push({
    sectionTitle: 'Business Partner',
    hidden: userAccess === 'POS',
    inputs: {
      businessPartnerId: {
        name: 'businessPartnerId',
        title: 'Business Partner',
        type: FormFieldType.select,
        placeholder: 'Business Partner',
        value: data.businessPartnerId,
        selectOptions: lists.partners,
        required: true
      }
    }
  });

  // Plan section
  sections.push({
    sectionTitle: 'Plan',
    inputs: {
      renewWithExistingPlan: {
        name: 'renewWithExistingPlan',
        title: 'Would you like to renew this Policy with its existing plan?',
        type: FormFieldType.switch,
        value: data.renewWithExistingPlan,
        required: false,
        hidden: !data.isRenewal
      },
      line: {
        name: 'line',
        title: 'Line',
        type: FormFieldType.select,
        placeholder: 'Line',
        value: data.line || Object.keys(getFilteredLineOptions())[0] || '',
        selectOptions: getFilteredLineOptions(),
        required: true,
        hidden: true
      },
      subline: {
        name: 'subline',
        title: 'Subline',
        type: FormFieldType.select,
        placeholder: 'Subline',
        value: !isEmpty(data.subline)
          ? data.subline
          : Object.keys(lists.subline)[0] || '',
        required: true,
        selectOptions: lists.subline,
        hidden: true
      },
      plan: {
        name: 'plan',
        title: 'Plan',
        type: FormFieldType.select,
        placeholder: 'Plan',
        value: !isEmpty(data.plan) ? data.plan : '',
        required: true,
        selectOptions: lists.plan
      },
      policyCurrency: {
        name: 'policyCurrency',
        title: 'Currency',
        type: FormFieldType.select,
        placeholder: 'Currency',
        value: data.policyCurrency || '',
        required: true,
        selectOptions: lists.planCurrency
      },
      isGroup: {
        name: 'isGroup',
        title: 'Group Policy',
        type: FormFieldType.switch,
        placeholder: 'Group Policy',
        value: data.isGroup,
        required: false,
        hidden: !showGroupPolicy || data.isRenewal
      }
    }
  });

  return sections;
};

// Export Proposals Drawer Constants
export const ALL = '__ALL__';

export const buildExportProposalsDrawerSections = (
  values: IExportProposalsFilters,
  lovs: Record<string, Record<string, string>>
): IDynamicFormWidgetSection[] => [
  {
    inputs: {
      user: {
        name: 'user',
        title: 'User',
        type: FormFieldType.multiAutocomplete,
        placeholder: values?.user?.length ? '' : 'Select User',
        value: values?.user ?? [ALL],
        required: true
      },
      businessPartner: {
        name: 'businessPartner',
        title: 'Business Partner',
        type: FormFieldType.multiSelect,
        placeholder: 'Select Business Partner',
        selectOptions: lovs['BusinessPartner'],
        value: values?.businessPartner ?? [ALL],
        required: true
      },
      currency: {
        name: 'currency',
        title: 'Currency',
        type: FormFieldType.multiSelect,
        placeholder: 'Select Currency',
        selectOptions: lovs['Currency'],
        value: values?.currency ?? [ALL],
        required: true
      },
      status: {
        name: 'status',
        title: 'Status',
        type: FormFieldType.multiSelect,
        placeholder: 'Select Status',
        selectOptions: lovs['Status'],
        value: values?.status ?? [ALL],
        required: true
      },
      policyEffectiveDateRange: {
        name: 'policyEffectiveDateRange',
        title: 'Policy Effective Date',
        type: FormFieldType.dateRange,
        placeholder: 'Select Policy Effective Date Range',
        value: values?.policyEffectiveDateRange ?? [],
        required: false
      },
      policyExpiryDateRange: {
        name: 'policyExpiryDateRange',
        title: 'Policy Expiry Date',
        type: FormFieldType.dateRange,
        placeholder: 'Select Policy Expiry Date Range',
        value: values?.policyExpiryDateRange ?? [],
        required: false
      },
      policyIssueDateRange: {
        name: 'policyIssueDateRange',
        title: 'Policy Issue Date',
        type: FormFieldType.dateRange,
        placeholder: 'Select Policy Issue Date Range',
        value: values?.policyIssueDateRange ?? [],
        required: false
      },
      dateRange: {
        name: 'dateRange',
        title: 'Created On',
        type: FormFieldType.dateRange,
        placeholder: 'Select Date Range',
        value: values?.dateRange ?? [],
        required: false
      }
    }
  }
];

export const exportProposalsExcelHeaders = {
  name: { label: 'Name', visible: true, isDate: false, isCurrency: false },
  plan: { label: 'Plan', visible: true, isDate: false, isCurrency: false },
  user: { label: 'User', visible: true, isDate: false, isCurrency: false },
  broker: { label: 'Broker', visible: true, isDate: false, isCurrency: false },
  policyEffectiveDate: {
    label: 'Policy Effective Date',
    visible: true,
    isDate: true,
    isCurrency: false
  },
  policyExpiryDate: {
    label: 'Policy Expiry Date',
    visible: true,
    isDate: true,
    isCurrency: false
  },
  policyIssueDate: {
    label: 'Policy Issue Date',
    visible: true,
    isDate: true,
    isCurrency: false
  },
  currency: {
    label: 'Currency',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  isRenewal: {
    label: 'Is Renewal',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  renewalFrom: {
    label: 'Renewal From',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  status: { label: 'Status', visible: true, isDate: false, isCurrency: false },
  statusReason: {
    label: 'Status Reason',
    visible: true,
    isDate: false,
    isCurrency: false
  }
};

export const exportProposalsExcelHeaderGroups = [
  { title: 'Basic Details', fromKey: 'name', toKey: 'broker' },
  {
    title: 'Proposal Information',
    fromKey: 'policyEffectiveDate',
    toKey: 'currency'
  },
  { title: 'Renewal Details', fromKey: 'isRenewal', toKey: 'renewalFrom' },
  { title: 'Status Information', fromKey: 'status', toKey: 'statusReason' }
];

export const exportProposalsExcelColumnWidths = [
  25, // Name
  30, // Plan
  25, // User
  30, // Broker
  25, // Policy Effective Date
  25, // Policy Expiry Date
  25, // Policy Issue Date
  20, // Currency
  15, // Is Renewal
  25, // Renewal From
  20, // Status
  25 // Status Reason
];

export const exportProposalsExcelStyles = {
  groupHeader: {
    font: { sz: 11, bold: true, color: { rgb: 'FFFFFFFF' } },
    fill: { patternType: 'solid', fgColor: { rgb: '215c98' } },
    alignment: { horizontal: 'center', vertical: 'center' }
  },
  header: {
    font: { sz: 11, bold: true, color: { rgb: 'FF000000' } },
    fill: { patternType: 'solid', fgColor: { rgb: 'dae9f8' } },
    alignment: { horizontal: 'left', vertical: 'center', wrapText: true }
  }
};

export const closeProposalInitialValues: ICloseProposalInfo = {
  id: '',
  closingReason: ''
};

export const closeProposalInputs: Record<string, DynamicFormInputType> = {
  closingReason: {
    name: 'closingReason',
    title: 'Closing Reason',
    placeholder: 'Select Closing Reason',
    type: FormInputTypes.chips,
    value: !isEmpty(closeProposalInitialValues.closingReason)
      ? closeProposalInitialValues.closingReason
      : [],
    required: true,
    multiple: false,
    selectOptions: {
      'Premium is high': 'Premium is high',
      'Waiting for authority': 'Waiting for authority',
      'Above limits': 'Above limits'
    }
  }
};

export interface IExportPoliciesFilters {
  user: string[];
  broker: string[];
  currency: string[];
  status: string[];
  policyEffectiveDateRange: any[];
  policyExpiryDateRange: any[];
  policyIssueDateRange: any[];
  createdOn: any[];
}

export const buildExportPoliciesDrawerSections = (
  values: IExportPoliciesFilters,
  lovs: Record<string, Record<string, string>>
): IDynamicFormWidgetSection[] => [
  {
    inputs: {
      user: {
        name: 'user',
        title: 'User',
        type: FormFieldType.multiAutocomplete,
        placeholder: values?.user?.length ? '' : 'Select User',
        value: values?.user ?? [ALL],
        required: true
      },
      broker: {
        name: 'broker',
        title: 'Broker',
        type: FormFieldType.multiSelect,
        placeholder: 'Select Business Partner',
        selectOptions: lovs['Broker'],
        value: values?.broker ?? [ALL],
        required: true
      },
      currency: {
        name: 'currency',
        title: 'Currency',
        type: FormFieldType.multiSelect,
        placeholder: 'Select Currency',
        selectOptions: lovs['Currency'],
        value: values?.currency ?? [ALL],
        required: true
      },
      status: {
        name: 'status',
        title: 'Status',
        type: FormFieldType.multiSelect,
        placeholder: 'Select Status',
        selectOptions: lovs['Status'],
        value: values?.status ?? [ALL],
        required: true
      },
      policyEffectiveDateRange: {
        name: 'policyEffectiveDateRange',
        title: 'Policy Effective Date',
        type: FormFieldType.dateRange,
        placeholder: 'Select Policy Effective Date Range',
        value: values?.policyEffectiveDateRange ?? [],
        required: false
      },
      policyExpiryDateRange: {
        name: 'policyExpiryDateRange',
        title: 'Policy Expiry Date',
        type: FormFieldType.dateRange,
        placeholder: 'Select Policy Expiry Date Range',
        value: values?.policyExpiryDateRange ?? [],
        required: false
      },
      policyIssueDateRange: {
        name: 'policyIssueDateRange',
        title: 'Policy Issue Date',
        type: FormFieldType.dateRange,
        placeholder: 'Select Policy Issue Date Range',
        value: values?.policyIssueDateRange ?? [],
        required: false
      },
      createdOn: {
        name: 'createdOn',
        title: 'Created On',
        type: FormFieldType.dateRange,
        placeholder: 'Select Date Range',
        value: values?.createdOn ?? [],
        required: false
      }
    }
  }
];

export const exportPoliciesExcelHeaders = {
  policyNumber: {
    label: 'Policy Number',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  idNumber: {
    label: 'ID Number',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  effectiveDate: {
    label: 'Effective Date',
    visible: true,
    isDate: true,
    isCurrency: false
  },
  expirationDate: {
    label: 'Expiration Date',
    visible: true,
    isDate: true,
    isCurrency: false
  },
  name: { label: 'Name', visible: true, isDate: false, isCurrency: false },
  lastName: {
    label: 'Last Name',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  mobilePhone: {
    label: 'Mobile Phone',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  email: { label: 'Email', visible: true, isDate: false, isCurrency: false },
  address: {
    label: 'Address',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  city: { label: 'City', visible: true, isDate: false, isCurrency: false },
  state: { label: 'State', visible: true, isDate: false, isCurrency: false },
  zipCode: {
    label: 'ZipCode',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  dob: { label: 'DOB', visible: true, isDate: true, isCurrency: false },
  contractTier: {
    label: 'Contract Tier',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  relationCode: {
    label: 'Relation Code',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  totalPremium: {
    label: 'Total Premium',
    visible: true,
    isDate: false,
    isCurrency: true
  },
  status: {
    label: 'Status',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  relation: {
    label: 'Relation',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  sumInsured: {
    label: 'Sum Insured',
    visible: true,
    isDate: false,
    isCurrency: true
  }
};

export const exportPoliciesExcelColumnWidths = [
  20, // Policy Number
  20, // ID Number
  18, // Effective Date
  18, // Expiration Date
  20, // Name
  20, // Last Name
  20, // Mobile Phone
  25, // Email
  30, // Address
  20, // City
  15, // State
  15, // ZipCode
  18, // DOB
  30, // Contract Tier
  18, // Relation Code
  20, // Total Premium
  20, // Status
  20, // Relation
  20 // Sum Insured
];

export const exportPoliciesExcelStyles = {
  groupHeader: {
    font: { sz: 11, bold: true, color: { rgb: 'FFFFFFFF' } },
    fill: { patternType: 'solid', fgColor: { rgb: '215c98' } },
    alignment: { horizontal: 'center', vertical: 'center' }
  },
  header: {
    font: { sz: 11, bold: true, color: { rgb: 'FF000000' } },
    fill: { patternType: 'solid', fgColor: { rgb: 'D9D9D9' } },
    alignment: { horizontal: 'left', vertical: 'center', wrapText: true }
  }
};

// Export Amendments Drawer Constants
export interface IExportAmendmentsFilters {
  user: string[];
  status: string[];
  amendmentEffectiveDateRange: any[];
  createdOn: any[];
}

export const buildExportAmendmentsDrawerSections = (
  values: IExportAmendmentsFilters,
  lovs: Record<string, Record<string, string>>
): IDynamicFormWidgetSection[] => [
  {
    inputs: {
      user: {
        name: 'user',
        title: 'User Created By',
        type: FormFieldType.multiAutocomplete,
        placeholder: values?.user?.length ? '' : 'Select Amendment Created By',
        value: values?.user ?? [ALL],
        required: true
      },
      status: {
        name: 'status',
        title: 'Status',
        type: FormFieldType.multiSelect,
        placeholder: 'Select Amendment Status',
        selectOptions: lovs['Status'],
        value: values?.status ?? [ALL],
        required: true
      },
      amendmentEffectiveDateRange: {
        name: 'amendmentEffectiveDateRange',
        title: 'Amendment Effective Date',
        type: FormFieldType.dateRange,
        placeholder: 'Select Date Range',
        value: values?.amendmentEffectiveDateRange ?? [],
        required: false
      },
      createdOn: {
        name: 'createdOn',
        title: 'Created On',
        type: FormFieldType.dateRange,
        placeholder: 'Select Date Range',
        value: values?.createdOn ?? [],
        required: false
      }
    }
  }
];

export const exportAmendmentsExcelHeaders = {
  amendmentNumber: {
    label: 'Amendment Number',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  amendmentType: {
    label: 'Amendment Type',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  amendmentReason: {
    label: 'Amendment Reason',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  amendmentEffectiveDate: {
    label: 'Amendment Effective Date',
    visible: true,
    isDate: true,
    isCurrency: false
  },
  plan: { label: 'Plan', visible: true, isDate: false, isCurrency: false },
  user: { label: 'User', visible: true, isDate: false, isCurrency: false },
  broker: { label: 'Broker', visible: true, isDate: false, isCurrency: false },
  policyNumber: {
    label: 'Policy Number',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  policyEffectiveDate: {
    label: 'Policy Effective Date',
    visible: true,
    isDate: true,
    isCurrency: false
  },
  policyExpiryDate: {
    label: 'Policy Expiry Date',
    visible: true,
    isDate: true,
    isCurrency: false
  },
  policyIssueDate: {
    label: 'Policy Issue Date',
    visible: true,
    isDate: true,
    isCurrency: false
  },
  currency: {
    label: 'Currency',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  status: { label: 'Status', visible: true, isDate: false, isCurrency: false },
  statusReason: {
    label: 'Status Reason',
    visible: true,
    isDate: false,
    isCurrency: false
  }
};

export const exportAmendmentsExcelHeaderGroups = [
  {
    title: 'Amendment Information',
    fromKey: 'amendmentNumber',
    toKey: 'broker'
  },
  {
    title: 'Policy Information',
    fromKey: 'policyNumber',
    toKey: 'currency'
  },
  { title: 'Status Information', fromKey: 'status', toKey: 'statusReason' }
];

export const exportAmendmentsExcelColumnWidths = [
  25, // Amendment Number
  25, // Amendment Type
  30, // Amendment Reason
  25, // Amendment Effective Date
  25, // Plan
  25, // User
  30, // Broker
  25, // Policy Number
  25, // Policy Effective Date
  25, // Policy Expiry Date
  25, // Policy Issue Date
  20, // Currency
  20, // Status
  25 // Status Reason
];

export const exportAmendmentsExcelStyles = {
  groupHeader: {
    font: { sz: 11, bold: true, color: { rgb: 'FFFFFFFF' } },
    fill: { patternType: 'solid', fgColor: { rgb: '215c98' } },
    alignment: { horizontal: 'center', vertical: 'center' }
  },
  header: {
    font: { sz: 11, bold: true, color: { rgb: 'FF000000' } },
    fill: { patternType: 'solid', fgColor: { rgb: 'dae9f8' } },
    alignment: { horizontal: 'left', vertical: 'center', wrapText: true }
  }
};

// Modify Amendment Drawer Constants
export interface IModifyAmendmentFormValues {
  amendmentReason: string;
  amendmentIssueDate: string;
  amendmentEffectiveDate: string;
}

export const buildModifyAmendmentDrawerSections = ({
  showReason,
  amendmentReason,
  amendmentIssueDate,
  amendmentEffectiveDate,
  minDate,
  maxDate,
  issueDateLabel,
  effectiveDateLabel
}: {
  showReason: boolean;
  amendmentReason: string;
  amendmentIssueDate: string;
  amendmentEffectiveDate: string;
  minDate: Date | undefined;
  maxDate: Date | undefined;
  issueDateLabel?: string;
  effectiveDateLabel?: string;
}): IDynamicFormWidgetSection[] => [
  {
    inputs: {
      ...(showReason && {
        amendmentReason: {
          name: 'amendmentReason',
          title: 'Cancellation Reason',
          placeholder: 'Cancellation Reason',
          type: FormFieldType.text,
          value: amendmentReason,
          required: true,
          disabled: false
        }
      }),
      amendmentIssueDate: {
        name: 'amendmentIssueDate',
        title: issueDateLabel?.replace('*', '') || 'Amendment Issue Date',
        placeholder: issueDateLabel || 'Amendment Issue Date',
        type: FormFieldType.date,
        value: amendmentIssueDate,
        required: true,
        disabled: true
      },
      amendmentEffectiveDate: {
        name: 'amendmentEffectiveDate',
        title:
          effectiveDateLabel?.replace('*', '') || 'Amendment Effective Date',
        placeholder: effectiveDateLabel || 'Amendment Effective Date',
        type: FormFieldType.date,
        value: amendmentEffectiveDate,
        required: true,
        disabled: true,
        minDate: minDate,
        maxDate: maxDate
      }
    }
  }
];

// New Claim Drawer Constants
export type INewClaimDrawerValues = {
  policyId: string;
  declarationDate: Date | string;
  claimDate: Date | string;
  insuredId: string;
};

export type INewClaimDrawerLovs = {
  policies: Record<string, string>;
  insureds: Record<string, string>;
};

export const getNewClaimDrawerInitialValues = (): INewClaimDrawerValues => ({
  policyId: '',
  declarationDate: new Date(),
  claimDate: new Date(),
  insuredId: ''
});

export const buildNewClaimDrawerSections = (
  values: INewClaimDrawerValues = getNewClaimDrawerInitialValues(),
  lists: INewClaimDrawerLovs = {
    policies: {},
    insureds: {}
  },
  bounds?: {
    declarationDateMinDate?: Date;
    declarationDateMaxDate?: Date;
    claimDateMinDate?: Date;
    claimDateMaxDate?: Date;
  }
): IDynamicFormWidgetSection[] => [
  {
    inputs: {
      policyId: {
        name: 'policyId',
        title: 'Policy',
        type: FormFieldType.autocomplete,
        placeholder: 'Select Policy',
        value: values.policyId,
        disabled: false,
        required: true,
        initialOptions: lists.policies,
        canClearValue: true
      },
      declarationDate: {
        name: 'declarationDate',
        title: 'Declaration Date',
        placeholder: 'Enter Declaration Date',
        type: FormFieldType.date,
        value: values.declarationDate,
        minDate: bounds?.declarationDateMinDate,
        maxDate: bounds?.declarationDateMaxDate ?? new Date(),
        required: true
      },
      claimDate: {
        name: 'claimDate',
        title: 'Claim Date',
        placeholder: 'Enter Claim Date',
        type: FormFieldType.date,
        value: values.claimDate,
        minDate: bounds?.claimDateMinDate,
        maxDate: bounds?.claimDateMaxDate,
        required: true
      },
      insuredId: {
        name: 'insuredId',
        title: 'Insured',
        type: FormFieldType.select,
        placeholder: 'Select Insured',
        value: values.insuredId,
        disabled:
          !values.policyId || Object.keys(lists.insureds || {})?.length <= 1,
        required: true,
        selectOptions: lists.insureds
      }
    }
  }
];

export interface IExportClaimsFilters {
  policy: string[];
  status: string[];
  claimDateRange: any[];
  declarationDateRange: any[];
  createdOnDateRange: any[];
}

export const buildExportClaimsDrawerSections = (
  values: IExportClaimsFilters,
  lovs: Record<string, Record<string, string>>
): IDynamicFormWidgetSection[] => [
  {
    inputs: {
      policy: {
        name: 'policy',
        title: 'Policy Number',
        type: FormFieldType.multiSelect,
        placeholder: 'Select Policy',
        selectOptions: lovs['Policy'],
        value: values?.policy ?? [ALL],
        required: true
      },
      status: {
        name: 'status',
        title: 'Status',
        type: FormFieldType.multiSelect,
        placeholder: 'Select Status',
        selectOptions: lovs['Status'],
        value: values?.status ?? [ALL],
        required: true
      },
      claimDateRange: {
        name: 'claimDateRange',
        title: 'Claim Date',
        type: FormFieldType.dateRange,
        placeholder: 'Select Claim Date Range',
        value: values?.claimDateRange ?? [],
        required: false
      },
      declarationDateRange: {
        name: 'declarationDateRange',
        title: 'Declaration Date',
        type: FormFieldType.dateRange,
        placeholder: 'Select Declaration Date Range',
        value: values?.declarationDateRange ?? [],
        required: false
      },
      createdOnDateRange: {
        name: 'createdOnDateRange',
        title: 'Created On Date',
        type: FormFieldType.dateRange,
        placeholder: 'Select Date Range',
        value: values?.createdOnDateRange ?? [],
        required: false
      }
    }
  }
];

export const exportClaimsExcelHeaders = {
  policyNumber: {
    label: 'Policy Number',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  policyStatus: {
    label: 'Policy Status',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  policyEffectiveDate: {
    label: 'Policy Effective Date',
    visible: true,
    isDate: true,
    isCurrency: false
  },
  policyExpiryDate: {
    label: 'Policy Expiry Date',
    visible: true,
    isDate: true,
    isCurrency: false
  },
  policyIssueDate: {
    label: 'Policy Issue Date',
    visible: true,
    isDate: true,
    isCurrency: false
  },
  currency: {
    label: 'Currency',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  claimNumber: {
    label: 'Claim Number',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  claimDate: {
    label: 'Claim Date',
    visible: true,
    isDate: true,
    isCurrency: false
  },
  declarationDate: {
    label: 'Declaration Date',
    visible: true,
    isDate: true,
    isCurrency: false
  },
  createdOn: {
    label: 'Created On',
    visible: true,
    isDate: true,
    isCurrency: false
  },
  status: {
    label: 'Status',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  statusReason: {
    label: 'Status Reason',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  insured: {
    label: 'Insured',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  notes: { label: 'Notes', visible: true, isDate: false, isCurrency: false },
  totalEstimations: {
    label: 'Total Estimations',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  totalApproved: {
    label: 'Total Approved',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  totalPaid: {
    label: 'Total Paid',
    visible: true,
    isDate: false,
    isCurrency: false
  }
};

export const exportClaimsExcelHeaderGroups = [
  { title: 'Policy Information', fromKey: 'policyNumber', toKey: 'currency' },
  { title: 'Claim Information', fromKey: 'claimNumber', toKey: 'notes' },
  { title: 'Claim Details', fromKey: 'totalEstimations', toKey: 'totalPaid' }
];

export const exportClaimsExcelColumnWidths = [
  25, // Policy Number
  20, // Policy Status
  25, // Policy Effective Date
  25, // Policy Expiry Date
  25, // Policy Issue Date
  20, // Currency
  25, // Claim Number
  25, // Claim Date
  25, // Declaration Date
  25, // Created On
  20, // Status
  25, // Status Reason
  25, // Insured
  30, // Notes
  25, // Total Estimations
  25, // Total Approved
  25 // Total Paid
];

export const exportClaimsExcelStyles = {
  groupHeader: {
    font: { sz: 11, bold: true, color: { rgb: 'FFFFFFFF' } },
    fill: { patternType: 'solid', fgColor: { rgb: '215c98' } },
    alignment: { horizontal: 'center', vertical: 'center' }
  },
  header: {
    font: { sz: 11, bold: true, color: { rgb: 'FF000000' } },
    fill: { patternType: 'solid', fgColor: { rgb: 'dae9f8' } },
    alignment: { horizontal: 'left', vertical: 'center', wrapText: true }
  }
};

// Export Reinsurers Drawer Constants
export interface IExportReinsurersFilters {
  relatedCompany: string[];
  isLocal: string[];
  status: string[];
  createdOn: any[];
}

export const exportReinsurersDrawerSections = (
  values: IExportReinsurersFilters,
  lovs: Record<string, Record<string, string>>
): IDynamicFormWidgetSection[] => {
  return [
    {
      sectionTitle: 'Information',
      inputs: {
        isLocal: {
          name: 'isLocal',
          title: 'Is Local',
          type: FormFieldType.multiSelect,
          placeholder: 'Is Local',
          selectOptions: lovs['YesNoList'],
          value: values?.isLocal ?? [ALL],
          required: true
        },
        status: {
          name: 'status',
          title: 'Status',
          type: FormFieldType.multiSelect,
          placeholder: 'Status',
          selectOptions: lovs['BusinessPartnerStatusList'],
          value: values?.status ?? [ALL],
          required: true
        },
        createdOn: {
          name: 'createdOn',
          title: 'Created On',
          type: FormFieldType.dateRange,
          placeholder: 'Select Date Range',
          value: values?.createdOn ?? [],
          required: false
        }
      }
    }
  ];
};

export const exportReinsurersExcelHeaders = {
  code: { label: 'Code', visible: true, isDate: false, isCurrency: false },
  fullName: {
    label: 'Full Name',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  isCompany: {
    label: 'Is Company',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  isLocal: {
    label: 'Is Local',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  isRegistered: {
    label: 'Is Registered',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  registrationNumber: {
    label: 'Registration Number',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  registrationExpiryDate: {
    label: 'Registration Expiry Date',
    visible: true,
    isDate: true,
    isCurrency: false
  },
  taxAuxiliaryAccount: {
    label: 'Tax Auxiliary Account',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  auxiliaryAccount: {
    label: 'Auxiliary Account',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  status: { label: 'Status', visible: true, isDate: false, isCurrency: false }
};

export const exportReinsurersExcelHeaderGroups = [
  {
    title: 'Reinsurer Details',
    fromKey: 'code',
    toKey: 'registrationExpiryDate'
  },
  {
    title: 'Auxiliary Details',
    fromKey: 'taxAuxiliaryAccount',
    toKey: 'auxiliaryAccount'
  },
  { title: 'Status Details', fromKey: 'status', toKey: 'status' }
];

export const exportReinsurersExcelColumnWidths = [
  16, // Code
  26, // Full Name
  14, // Is Company
  14, // Is Local
  16, // Is Registered
  22, // Registration Number
  22, // Registration Expiry Date
  22, // Tax Auxiliary Account
  22, // Auxiliary Account
  22 // Status
];

export const exportReinsurersExcelTemplateStyles = {
  groupHeader: {
    font: { sz: 11, bold: true, color: { rgb: 'FFFFFFFF' } },
    fill: { patternType: 'solid', fgColor: { rgb: 'FF1F4E79' } },
    alignment: { horizontal: 'center', vertical: 'center' }
  },
  header: {
    font: { sz: 11, bold: true, color: { rgb: 'FF000000' } },
    fill: { patternType: 'solid', fgColor: { rgb: 'FFD9E1F2' } },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true }
  }
};

// =============== TREATIES EXPORT CONSTANTS ===============
export interface IExportTreatiesFilters {
  relatedCompany: string[];
  treatyType: string[];
  treatyStatus: string[];
  effectiveDate: any[];
  treatyCreatedOn: any[];
}

export const exportTreatiesDrawerSections = (
  values: IExportTreatiesFilters,
  lovs: Record<string, Record<string, string>>
): IDynamicFormWidgetSection[] => {
  return [
    {
      sectionTitle: 'Information',
      inputs: {
        relatedCompany: {
          name: 'relatedCompany',
          title: 'Related Company',
          type: FormFieldType.multiSelect,
          placeholder: 'Related Company',
          selectOptions: lovs['RelatedCompanyList'],
          value: values?.relatedCompany ?? [ALL],
          required: true,
          hidden: true
        },
        treatyType: {
          name: 'treatyType',
          title: 'Type',
          type: FormFieldType.multiSelect,
          placeholder: 'Type',
          selectOptions: lovs['TreatyTypeList'],
          value: values?.treatyType ?? [ALL],
          required: true
        },
        treatyStatus: {
          name: 'treatyStatus',
          title: 'Status',
          type: FormFieldType.multiSelect,
          placeholder: 'Status',
          selectOptions: lovs['TreatyStatusList'],
          value: values?.treatyStatus ?? [ALL],
          required: true
        },
        effectiveDate: {
          name: 'effectiveDate',
          title: 'Treaty Effective Date',
          type: FormFieldType.dateRange,
          placeholder: 'Select Effective Date Range',
          value: values?.effectiveDate ?? [],
          required: false
        },
        treatyCreatedOn: {
          name: 'treatyCreatedOn',
          title: 'Created On',
          type: FormFieldType.dateRange,
          placeholder: 'Select Date Range',
          value: values?.treatyCreatedOn ?? [],
          required: false
        }
      }
    }
  ];
};

export const exportTreatiesExcelHeaders = {
  code: { label: 'Code', visible: true, isDate: false, isCurrency: false },
  name: { label: 'Name', visible: true, isDate: false, isCurrency: false },
  type: { label: 'Type', visible: true, isDate: false, isCurrency: false },
  currency: {
    label: 'Currency',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  status: { label: 'Status', visible: true, isDate: false, isCurrency: false },
  treatyEffectiveDate: {
    label: 'Treaty Effective Date',
    visible: true,
    isDate: true,
    isCurrency: false
  },
  treatyExpiryDate: {
    label: 'Treaty Expiry Date',
    visible: true,
    isDate: true,
    isCurrency: false
  },
  underwritingLimit: {
    label: 'Underwriting Limit',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  substandardLimit: {
    label: 'Substandard Limit',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  claimAdviseLimit: {
    label: 'Claim Advise Limit',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  leaderReinsurer: {
    label: 'Leader Reinsurer',
    visible: true,
    isDate: false,
    isCurrency: false
  }
};

export const exportTreatiesExcelHeaderGroups = [
  { title: 'Basic Details', fromKey: 'code', toKey: 'status' },
  {
    title: 'Basic Details',
    fromKey: 'treatyEffectiveDate',
    toKey: 'treatyExpiryDate'
  },
  {
    title: 'Informative',
    fromKey: 'underwritingLimit',
    toKey: 'claimAdviseLimit'
  },
  {
    title: 'Main Reinsurer',
    fromKey: 'leaderReinsurer',
    toKey: 'leaderReinsurer'
  }
];

export const exportTreatiesExcelColumnWidths = [
  14, // Code
  30, // Name
  16, // Type
  14, // Currency
  14, // Status
  22, // Treaty Effective Date
  22, // Treaty Expiry Date
  18, // Underwriting Limit
  18, // Substandard Limit
  18, // Claim Advise Limit
  26 // Leader Reinsurer
];

export const exportTreatiesExcelTemplateStyles = {
  groupHeader: {
    font: { sz: 11, bold: true, color: { rgb: 'FFFFFFFF' } },
    fill: { patternType: 'solid', fgColor: { rgb: 'FF1F4E79' } },
    alignment: { horizontal: 'center', vertical: 'center' }
  },
  header: {
    font: { sz: 11, bold: true, color: { rgb: 'FF000000' } },
    fill: { patternType: 'solid', fgColor: { rgb: 'FFD9E1F2' } },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true }
  }
};

// New Treaty Constants
export const DEFAULT_CURRENCY_ID = '9865b439-3194-4b39-ad2e-d2706384e312';

export const treatyTypes = {
  PROPORTIONAL: 'Proportional',
  NON_PROPORTIONAL: 'Non-Proportional'
};

export const newTreatyDrawerSections = (
  values: any,
  lovs: any
): IDynamicFormWidgetSection[] => {
  const selectedIds = (values?.reinsurersRepeater || []).map(
    (r: any) => r.reinsurer
  );

  const source = lovs?.reinsurerOptions || {};

  const reinsurerList = Object.fromEntries(
    Object.entries(source).filter(([id]) => {
      const keep =
        !selectedIds?.includes(id) ||
        (values?.reinsurersRepeater || []).some(
          (r: any) => r?.reinsurer === id
        );

      return keep;
    })
  ) as Record<string, string>;

  return [
    {
      sectionTitle: 'Treaty Information',
      inputs: {
        company: {
          name: 'company',
          title: 'Company',
          placeholder: 'Select Company',
          type: FormFieldType.select,
          selectOptions: lovs?.company || {},
          value: values?.company,
          required: false,
          hidden: true
        },
        treatyName: {
          name: 'treatyName',
          title: 'Name',
          placeholder: 'Enter Treaty Name',
          type: FormFieldType.text,
          value: values?.treatyName,
          required: true
        },
        treatyType: {
          name: 'treatyType',
          title: 'Type',
          placeholder: 'Select Treaty Type',
          type: FormFieldType.select,
          selectOptions: lovs?.treatyType || {},
          value: values?.treatyType,
          required: true
        },
        currency: {
          name: 'currency',
          title: 'Currency',
          placeholder: 'Select Treaty Currency',
          type: FormFieldType.select,
          selectOptions: lovs?.currency || {},
          value: values?.currency ?? DEFAULT_CURRENCY_ID,
          required: true
        },
        effectiveDate: {
          name: 'effectiveDate',
          title: 'Treaty Effective Date',
          type: FormFieldType.date,
          value: values?.effectiveDate,
          required: true,
          minDate: new Date(),
          customValidator: (values) => {
            const isDateBefore = (date1: any, date2: any) => {
              if (!date1 || !date2) return false;

              return new Date(date1) < new Date(date2);
            };

            const isInvalid = isDateBefore(
              values?.expiryDate,
              values?.effectiveDate
            );

            return isInvalid ? 'cannot be greater than expiry date' : '';
          }
        },
        expiryDate: {
          name: 'expiryDate',
          title: 'Treaty Expiry Date',
          type: FormFieldType.date,
          value: values?.expiryDate,
          required: true,
          minDate: values?.effectiveDate
            ? new Date(
                new Date(values.effectiveDate).getTime() + 24 * 60 * 60 * 1000
              )
            : new Date(),
          customValidator: (values) => {
            const isDateBefore = (date1: any, date2: any) => {
              if (!date1 || !date2) return false;

              return new Date(date1) < new Date(date2);
            };

            const isInvalid = isDateBefore(
              values?.expiryDate,
              values?.effectiveDate
            );

            return isInvalid ? 'cannot be less than effective date' : '';
          }
        },
        underwritingLimit: {
          name: 'underwritingLimit',
          title: 'Underwriting Limit',
          placeholder: 'Enter Underwriting Limit',
          type: FormFieldType.currency,
          currencySymbol:
            lovs.currencySymbols?.[values?.currency ?? DEFAULT_CURRENCY_ID],
          value: values?.underwritingLimit,
          required: true
        },
        claimadviseLimit: {
          name: 'claimadviseLimit',
          title: 'Claim Advise Limit',
          placeholder: 'Enter Claim Advise Limit',
          type: FormFieldType.currency,
          currencySymbol:
            lovs.currencySymbols?.[values?.currency ?? DEFAULT_CURRENCY_ID],
          value: values?.claimadviseLimit,
          required: true
        },
        substandardLimit: {
          name: 'substandardLimit',
          title: 'Substandard Limit*',
          placeholder: 'Enter Substandard Limit',
          type: FormFieldType.number,
          value: values?.substandardLimit,
          required: false,
          hidden: values?.treatyType?.toLowerCase() !== 'proportional',
          minValue: 0,
          customValidator: (values) => {
            if (Number(values?.substandardLimit) > 999) {
              return 'cannot be greater than 999';
            }

            return '';
          }
        }
      }
    },
    {
      sectionTitle: 'Reinsurers',
      inputs: {
        reinsurersRepeater: {
          name: 'reinsurersRepeater',
          title: '',
          type: FormFieldType.repeaterBox,
          filterDuplicatesByKey: 'reinsurer',
          addButtonText: '+Add Reinsurer',
          value: values?.reinsurersRepeater,
          maxRepeaterLength: 999,
          minRepeaterLength: 1,
          inputs: {
            reinsurer: {
              name: 'reinsurer',
              title: 'Additional Reinsurer',
              dynamicTitle: ['Leader Reinsurer*'],
              placeholder: 'Select Reinsurer',
              type: FormFieldType.select,
              required: true,
              value: '',
              selectOptions: reinsurerList,
              customValidator: (formValues) => {
                const reinsurerIds = formValues.reinsurersRepeater.map(
                  (item: any) => item.reinsurer
                );
                const isDuplicated = reinsurerIds.some(
                  (id: string, index: number) =>
                    reinsurerIds.indexOf(id) !== index
                );

                return isDuplicated ? 'Reinsurer already selected' : '';
              }
            },
            share: {
              name: 'share',
              title: 'Share',
              dynamicTitle: ['Share*'],
              placeholder: 'Enter Share',
              type: FormFieldType.percentage,
              value: '',
              error: '',
              required: true,
              customValidator: (values) => {
                const totalShare = values.reinsurersRepeater.reduce(
                  (acc: number, item: any) => acc + parseFloat(item.share || 0),
                  0
                );

                return totalShare !== 100
                  ? 'Total Share should be equal to 100%'
                  : '';
              }
            }
          }
        }
      }
    }
  ];
};

export const updateReinsurersDrawerSections = (
  value: IReinsurerValues[],
  reinsurerList: Record<string, string>,
  maxRepeaterLength: number
): IDynamicFormWidgetSection[] => [
  {
    inputs: {
      reinsurersRepeater: {
        name: 'reinsurersRepeater',
        title: '',
        type: FormFieldType.repeaterBox,
        addButtonText: '+Add Reinsurer',
        value: value,
        maxRepeaterLength: maxRepeaterLength,
        minRepeaterLength: 1,
        filterDuplicatesByKey: 'reinsurer',
        inputs: {
          reinsurer: {
            name: 'reinsurer',
            title: 'Additional Reinsurer', // This is the default title
            dynamicTitle: ['Leader Reinsurer*'], // Dynamic title based on the index
            placeholder: 'Type to search for Reinsurer',
            type: FormFieldType.select,
            value: '',
            error: '',
            required: true,
            selectOptions: reinsurerList
          },
          share: {
            name: 'share',
            title: 'Share',
            dynamicTitle: ['Share*'],
            placeholder: 'Enter Share',
            type: FormFieldType.percentage,
            value: '',
            error: '',
            required: true,
            customValidator: (values) => {
              const totalShare = values.reinsurersRepeater.reduce(
                (acc: number, item: any) => acc + parseFloat(item.share || 0),
                0
              );

              return totalShare !== 100
                ? 'Total Share should be equal to 100%'
                : '';
            }
          }
        }
      }
    }
  }
];

// Policy Distribution Constants
export const exportPolicyDistributionDrawerSections = (
  values: any,
  lovs: Record<string, Record<string, string>>
): IDynamicFormWidgetSection[] => [
  {
    inputs: {
      company: {
        name: 'company',
        title: 'Company',
        type: FormFieldType.multiSelect,
        placeholder: 'Select Company',
        selectOptions: lovs['company'],
        value: values?.company ?? [ALL],
        required: true
      },
      policyNumber: {
        name: 'policyNumber',
        title: 'Policy Number',
        type: FormFieldType.multiSelect,
        placeholder: 'Select Policy',
        selectOptions: lovs['policyNumber'],
        value: values?.policyNumber ?? [ALL],
        required: true
      },
      effectiveDate: {
        name: 'effectiveDate',
        title: 'Effective Date',
        type: FormFieldType.dateRange,
        placeholder: 'Select Effective Date Range',
        value: values?.effectiveDate ?? [],
        required: false
      },
      createdOn: {
        name: 'createdOn',
        title: 'Created On',
        type: FormFieldType.dateRange,
        placeholder: 'Select Date Range',
        value: values?.createdOn ?? [],
        required: false
      },
      treaty: {
        name: 'treaty',
        title: 'Treaty',
        type: FormFieldType.multiSelect,
        placeholder: 'Select Treaty',
        selectOptions: lovs['treaty'],
        value: values?.treaty ?? [ALL],
        required: true
      }
    }
  }
];

// Excel Template Configuration
export const excelHeaders = {
  policyNumber: {
    label: 'Policy Number',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  planCover: {
    label: 'Plan Cover',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  treaty: { label: 'Treaty', visible: true, isDate: false, isCurrency: false },
  treatyCurrency: {
    label: 'Treaty Currency',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  layerType: {
    label: 'Layer Type',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  layerOrder: {
    label: 'Layer Order',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  excessAmount: {
    label: 'Excess Amount',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  minimumLayerLimit: {
    label: 'Minimum Layer Limit',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  maximumLayerLimit: {
    label: 'Maximum Layer Limit',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  retentionlimit: {
    label: 'Retention Limit',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  retainedSumInsured: {
    label: 'Retained Sum Insured',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  retainedSumInsuredBasic: {
    label: 'Retained Sum Insured (Basic)',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  retainedSumInsuredSecondary: {
    label: 'Retained Sum Insured (Secondary)',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  cededSumInsured: {
    label: 'Ceded Sum Insured',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  cededSumInsuredBasic: {
    label: 'Ceded Sum Insured (Basic)',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  cededSumInsuredSecondary: {
    label: 'Ceded Sum Insured (Secondary)',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  retainedNetPremium: {
    label: 'Retained Net Premium',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  retainedNetPremiumBasic: {
    label: 'Retained Net Premium (Basic)',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  retainedNetPremiumSecondary: {
    label: 'Retained Net Premium (Secondary)',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  cededNetPremium: {
    label: 'Ceded Net Premium',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  cededNetPremiumBasic: {
    label: 'Ceded Net Premium (Basic)',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  cededNetPremiumSecondary: {
    label: 'Ceded Net Premium (Secondary)',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  commission: {
    label: 'Commission',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  commissionBasic: {
    label: 'Commission (Basic)',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  commissionSecondary: {
    label: 'Commission (Secondary)',
    visible: true,
    isDate: false,
    isCurrency: false
  }
};

export const excelFacsHeaders = {
  policyNumber: {
    label: 'Policy Number',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  planCover: {
    label: 'Plan Cover',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  cededSumInsured: {
    label: 'Ceded Sum Insured',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  cededSumInsuredBasic: {
    label: 'Ceded Sum Insured (Basic)',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  cededSumInsuredSecondary: {
    label: 'Ceded Sum Insured (Secondary)',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  cededNetPremium: {
    label: 'Ceded Net Premium',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  cededNetPremiumBasic: {
    label: 'Ceded Net Premium (Basic)',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  cededNetPremiumSecondary: {
    label: 'Ceded Net Premium (Secondary)',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  commission: {
    label: 'Commission',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  commissionBasic: {
    label: 'Commission (Basic)',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  commissionSecondary: {
    label: 'Commission (Secondary)',
    visible: true,
    isDate: false,
    isCurrency: false
  }
};

export const excelHeaderGroups = [
  {
    title: 'Policy Information',
    fromKey: 'policyNumber',
    toKey: 'excessAmount'
  },
  {
    title: 'Sum Insured',
    fromKey: 'minimumLayerLimit',
    toKey: 'cededSumInsuredSecondary'
  },
  {
    title: 'Net Premium',
    fromKey: 'retainedNetPremium',
    toKey: 'cededNetPremiumSecondary'
  },
  { title: 'Commission', fromKey: 'commission', toKey: 'commissionSecondary' }
];

export const excelFacsHeaderGroups = [
  { title: 'Policy Information', fromKey: 'policyNumber', toKey: 'planCover' },
  {
    title: 'Sum Insured',
    fromKey: 'cededSumInsured',
    toKey: 'cededSumInsuredSecondary'
  },
  {
    title: 'Net Premium',
    fromKey: 'cededNetPremium',
    toKey: 'cededNetPremiumSecondary'
  },
  { title: 'Commission', fromKey: 'commission', toKey: 'commissionSecondary' }
];

export const excelTemplateStyles = {
  groupHeader: {
    font: { bold: true, color: { rgb: 'FFFFFF' } },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { rgb: '4F81BD' } },
    alignment: { horizontal: 'center', vertical: 'middle' }
  },
  header: {
    font: { bold: true, color: { rgb: 'FFFFFF' } },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { rgb: '366092' } },
    alignment: { horizontal: 'center', vertical: 'middle' }
  }
};

export const excelColumnWidths = [
  15, // Policy Number
  20, // Plan Cover
  30, // Treaty
  15, // Treaty Currency
  15, // Layer Type
  10, // Layer Order
  15, // Excess Amount
  20, // Minimum Layer Limit
  20, // Maximum Layer Limit
  15, // Retention Limit
  20, // Retained Sum Insured
  25, // Retained Sum Insured (Basic)
  30, // Retained Sum Insured (Secondary)
  20, // Ceded Sum Insured
  25, // Ceded Sum Insured (Basic)
  30, // Ceded Sum Insured (Secondary)
  20, // Retained Net Premium
  25, // Retained Net Premium (Basic)
  30, // Retained Net Premium (Secondary)
  20, // Ceded Net Premium
  25, // Ceded Net Premium (Basic)
  30, // Ceded Net Premium (Secondary)
  15, // Commission
  20, // Commission (Basic)
  25 // Commission (Secondary)
];

// Reactivate Treaty Constants
export const reactivateTreatyDrawerSections = (
  values: any
): IDynamicFormWidgetSection[] => [
  {
    inputs: {
      NewTreatyExpiryDate: {
        name: 'NewTreatyExpiryDate',
        title: 'New Treaty Expiry Date',
        placeholder: 'Treaty Expiry Date',
        type: FormFieldType.date,
        value: values?.NewTreatyExpiryDate,
        required: true,
        minDate: new Date()
      }
    }
  }
];

// New Currency Rate Constants
export const newCurrencyRateDrawerSections = (
  values: any
): IDynamicFormWidgetSection[] => [
  {
    inputs: {
      effectiveDate: {
        name: 'effectiveDate',
        title: 'Effective Date',
        placeholder: 'DD/MM/YYYY HH:mm',
        type: FormFieldType.datetime,
        value: values?.effectiveDate,
        required: true,
        disabled: false,
        maxDate: null
      }
    }
  }
];

export const exportCurrencyDrawerSections = (
  values: IExportCurrencyDrawerFilters,
  lovs: Record<string, Record<string, string>>
): IDynamicFormWidgetSection[] => {
  return [
    {
      inputs: {
        personType: {
          name: 'baseCurrency',
          title: 'Base Currency',
          type: FormFieldType.multiSelect,
          placeholder: 'Base Currency',
          selectOptions: lovs['BaseCurrency'],
          value: values?.baseCurrency ?? [ALL],
          required: true
        },
        effectiveDate: {
          name: 'effectiveDate',
          title: 'Effective Date',
          type: FormFieldType.dateRange,
          placeholder: 'Select Date Range',
          value: values?.effectiveDate ?? [],
          required: false
        }
      }
    }
  ];
};

export const exportCurrencyExcelColumnWidths = [20.43, 20.43, 16.57, 13.0];

export const exportCurrencyExcelHeaderGroups = [
  { title: 'General Details', fromKey: 'baseCurrency', toKey: 'effectiveDate' }
];

export const exportCurrencyExcelHeaders = {
  baseCurrency: {
    label: 'Base Currency',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  targetCurrency: {
    label: 'targetCurrency',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  rate: { label: 'Rate', visible: true, isDate: false, isCurrency: false },
  effectiveDate: {
    label: 'Effective Date',
    visible: true,
    isDate: true,
    isCurrency: false
  }
};

export const exportCurrencyExcelTemplateStyles = {
  groupHeader: {
    font: { sz: 11, bold: true, color: { rgb: 'FFFFFFFF' } },
    fill: { patternType: 'solid', fgColor: { rgb: '215c98' } },
    alignment: { horizontal: 'center', vertical: 'center' }
  },
  header: {
    font: { sz: 11, bold: true, color: { rgb: 'FF000000' } },
    fill: { patternType: 'solid', fgColor: { rgb: 'dae9f8' } },
    alignment: { horizontal: 'left', vertical: 'center', wrapText: true }
  }
};

export const getTaxationDrawerInitialSections =
  (): IDynamicFormWidgetSection[] => [
    {
      inputs: {
        line: {
          name: 'line',
          title: 'Line',
          type: FormFieldType.select,
          placeholder: 'Line',
          value: [],
          disabled: false,
          required: true,
          selectOptions: {}
        },
        effectiveFrom: {
          name: 'effectiveFrom',
          title: 'Effective Date',
          placeholder: 'Effective Date',
          type: FormFieldType.date,
          value: '',
          minDate: new Date(),
          required: true
        } as IDateFormFieldProps,
        fixedStamp: {
          name: 'fixedStamp',
          title: 'Fixed Stamp',
          placeholder: 'Fixed Stamp',
          type: FormFieldType.currency,
          value: '',
          required: true,
          disabled: false
        },
        fixedStampCurrency: {
          name: 'fixedStampCurrency',
          title: 'Fixed Stamp Currency',
          type: FormFieldType.select,
          placeholder: 'Fixed Stamp Currency',
          value: [],
          disabled: false,
          required: true,
          selectOptions: {}
        },
        proportionalStamp: {
          name: 'proportionalStamp',
          title: 'Proportional Stamp',
          placeholder: 'Proportional Stamp',
          type: FormFieldType.percentage,
          value: '',
          required: true
        },
        municipalityTax: {
          name: 'municipalityTax',
          title: 'Municipality Tax',
          placeholder: 'Municipality Tax',
          type: FormFieldType.percentage,
          value: '',
          required: true
        },
        taxOnCommission: {
          name: 'taxOnCommission',
          title: 'Tax On Commission',
          placeholder: 'Tax On Commission',
          type: FormFieldType.percentage,
          value: '',
          required: true
        },
        reinsuranceTax: {
          name: 'reinsuranceTax',
          title: 'Reinsurance Tax',
          placeholder: 'Reinsurance Tax',
          type: FormFieldType.percentage,
          value: '',
          required: true
        },
        applicableToAllPlans: {
          name: 'applicableToAllPlans',
          title: 'Applicable to All Plans',
          type: FormFieldType.switch,
          value: true,
          disabled: false,
          required: true,
          placeholder: ''
        }
      }
    },
    {
      sectionTitle: 'Special Taxation',
      hidden: true,
      inputs: {
        customRepeater: {
          name: 'customRepeater',
          title: '',
          type: FormFieldType.repeaterBox,
          addButtonText: '+ Add Item',
          value: [
            {
              plan: '',
              fixedStampCurrency: '',
              fixedStamp: '',
              proportionalStamp: '',
              municipalityTax: '',
              taxOnCommission: '',
              reinsuranceTax: ''
            }
          ],
          maxRepeaterLength: 5,
          minRepeaterLength: 1,
          hidden: true,
          inputs: {
            plan: {
              name: 'plan',
              title: 'Plan*',
              type: FormFieldType.select,
              placeholder: 'Plan',
              value: [],
              disabled: false,
              required: true,
              selectOptions: {}
            },
            fixedStamp: {
              name: 'fixedStamp',
              title: 'Fixed Stamp*',
              placeholder: 'Fixed Stamp',
              type: FormFieldType.currency,
              value: '',
              required: true,
              disabled: false
            },
            fixedStampCurrency: {
              name: 'fixedStampCurrency',
              title: 'Fixed Stamp Currency*',
              type: FormFieldType.select,
              placeholder: 'Fixed Stamp Currency',
              value: [],
              disabled: false,
              required: true,
              selectOptions: {}
            },
            proportionalStamp: {
              name: 'proportionalStamp',
              title: 'Proportional Stamp*',
              placeholder: 'Proportional Stamp',
              type: FormFieldType.percentage,
              value: '',
              required: true
            },
            municipalityTax: {
              name: 'municipalityTax',
              title: 'Municipality Tax*',
              placeholder: 'Municipality Tax',
              type: FormFieldType.percentage,
              value: '',
              required: true
            },
            taxOnCommission: {
              name: 'taxOnCommission',
              title: 'Tax On Commission*',
              placeholder: 'Tax On Commission',
              type: FormFieldType.percentage,
              value: '',
              required: true
            },
            reinsuranceTax: {
              name: 'reinsuranceTax',
              title: 'Reinsurance Tax*',
              placeholder: 'Reinsurance Tax',
              type: FormFieldType.percentage,
              value: '',
              required: true
            }
          }
        }
      }
    }
  ];

export const exportTaxationDrawerSections = (
  values: IExportTaxationFilters,
  lovs: Record<string, Record<string, string>>
): IDynamicFormWidgetSection[] => [
  {
    inputs: {
      taxationLine: {
        name: 'taxationLine',
        title: 'Line',
        type: FormFieldType.multiSelect,
        placeholder: 'Line Type',
        selectOptions: lovs['Lines'],
        value: values?.taxationLine ?? [ALL],
        required: true
      },
      effectiveDate: {
        name: 'effectiveDate',
        title: 'Effective Date',
        type: FormFieldType.dateRange,
        placeholder: 'Select Date Range',
        value: values?.effectiveDate ?? [],
        required: false
      }
    }
  }
];

export const exportTaxationExcelHeaders = {
  line: { label: 'Line', visible: true, isDate: false, isCurrency: false },
  effectiveFrom: {
    label: 'Effective From',
    visible: true,
    isDate: true,
    isCurrency: false
  },
  effectiveTo: {
    label: 'Effective To',
    visible: true,
    isDate: true,
    isCurrency: false
  },
  fixedStampCurrency: {
    label: 'Fixed Stamp Currency',
    visible: true,
    isDate: false,
    isCurrency: true
  },
  fixedStamp: {
    label: 'Fixed Stamp',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  proportionalStamp: {
    label: 'Proportional Stamp',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  municipalityTax: {
    label: 'Municipality Tax',
    visible: true,
    isDate: false,
    isCurrency: true
  },
  taxOnCommission: {
    label: 'Tax on Commission',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  reinsuranceTax: {
    label: 'Reinsurance Tax',
    visible: true,
    isDate: false,
    isCurrency: true
  },
  applicableToAllPlans: {
    label: 'Applicable To All Plans',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  plan: {
    label: 'Plan',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  fixedStampCurrencyPlan: {
    label: 'Fixed Stamp Currency',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  fixedStampPlan: {
    label: 'Fixed Stamp',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  proportionalStampPlan: {
    label: 'Proportional Stamp',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  municipalityTaxPlan: {
    label: 'Municipality Tax',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  taxOnCommissionPlan: {
    label: 'Tax on Commission',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  reinsuranceTaxPlan: {
    label: 'Reinsurance Tax',
    visible: true,
    isDate: false,
    isCurrency: false
  }
};

export const exportTaxationExcelHeaderGroups = [
  { title: 'Line Taxation', fromKey: 'line', toKey: 'applicableToAllPlans' },
  {
    title: 'Plan Specific Taxation',
    fromKey: 'plan',
    toKey: 'reinsuranceTaxPlan'
  }
];

export const exportTaxationExcelTemplateStyles = {
  groupHeader: {
    font: { sz: 11, bold: true, color: { rgb: 'FFFFFFFF' } },
    fill: { patternType: 'solid', fgColor: { rgb: '215c98' } },
    alignment: { horizontal: 'center', vertical: 'center' }
  },
  header: {
    font: { sz: 11, bold: true, color: { rgb: 'FF000000' } },
    fill: { patternType: 'solid', fgColor: { rgb: 'dae9f8' } },
    alignment: { horizontal: 'left', vertical: 'center', wrapText: true }
  }
};

export const exportTaxationExcelColumnWidths = [
  20, 14.43, 16.57, 13.0, 13.0, 23.29, 21.0, 13.0, 22.71, 13.0, 18.0, 20.0,
  24.43, 13.0, 21.86, 13.0, 13.0
];

export const newBusinessPartnerInitialValues = {
  newPerson: true,
  type: '',
  title: '',
  firstName: '',
  middleName: '',
  lastName: '',
  dateOfBirth: '',
  companyName: '',
  person: '',
  email: '',
  mobileNumber: '',
  relatedCompany: '41',
  bpType: '',
  isCompany: false,
  isLocal: false,
  isRegistered: false,
  registerNumber: '',
  registrationExpiryDate: '',
  addressType: '',
  country: '',
  zone: '',
  city: '',
  street: '',
  building: '',
  addressDetails: '',
  addressDetailsArabic: '',
  taxAuxiliary: '',
  auxiliary: '',
  maxCreditLimitAllowedCurrency: '',
  maxCreditLimitAllowed: '',
  allowAccessToAllInsured: false
};

export const newBusinessPartnerDrawerSections = (
  defaultValues: IAbstractRecord,
  lovs: Record<string, Record<string, string>>
): IDynamicFormWidgetSection[] => {
  const isNewPerson = defaultValues?.newPerson === true;
  const isIndividual = defaultValues?.type === 'INDIVIDUAL';
  const isCompany = defaultValues?.type === 'COMPANY';

  return [
    {
      sectionTitle: 'Person Details',
      inputs: {
        newPerson: {
          name: 'newPerson',
          title: 'New Person',
          type: FormFieldType.switch,
          value: defaultValues.newPerson,
          required: false,
          hidden: true
        },
        type: {
          name: 'type',
          title: 'Type',
          placeholder: 'Person Type',
          type: FormFieldType.select,
          selectOptions: lovs.personTypes,
          value: defaultValues.type,
          required: true,
          hidden: false
        },
        title: {
          name: 'title',
          title: 'Title',
          placeholder: 'Title',
          type: FormFieldType.select,
          selectOptions: lovs.personTitles,
          value: defaultValues.title,
          hidden: !isIndividual,
          required: isIndividual
        },
        firstName: {
          name: 'firstName',
          title: 'First Name',
          placeholder: 'First Name',
          type: FormFieldType.text,
          value: defaultValues.firstName,
          hidden: !isIndividual,
          required: isIndividual
        },
        middleName: {
          name: 'middleName',
          title: 'Middle Name',
          placeholder: 'Middle Name',
          type: FormFieldType.text,
          value: defaultValues.middleName,
          hidden: !isIndividual,
          required: false
        },
        lastName: {
          name: 'lastName',
          title: 'Last Name',
          placeholder: 'Last Name',
          type: FormFieldType.text,
          value: defaultValues.lastName,
          hidden: !isIndividual,
          required: isIndividual
        },
        dateOfBirth: {
          name: 'dateOfBirth',
          title: 'Date of Birth',
          placeholder: 'Date of Birth',
          type: FormFieldType.date,
          value: defaultValues.dateOfBirth,
          hidden: !isIndividual,
          required: isIndividual,
          maxDate: new Date()
        },
        companyName: {
          name: 'companyName',
          title: 'Company Name',
          placeholder: 'Company Name',
          type: FormFieldType.text,
          value: defaultValues.companyName,
          hidden: !isCompany,
          required: isCompany
        },
        // person: {
        //   name: 'person',
        //   title: 'Person',
        //   placeholder: 'Person',
        //   type: FormFieldType.select,
        //   selectOptions: lovs.activePersons,
        //   value: defaultValues.person,
        //   hidden: true,
        //   required: true
        // },
        email: {
          name: 'email',
          title: 'Email',
          placeholder: 'Email',
          type: FormFieldType.text,
          value: defaultValues.email,
          disabled: !defaultValues.newPerson,
          required: true,
          customValidator: ({ email }) => {
            return email && !REGEX.email.test(email) ? 'Invalid Email' : '';
          }
        },
        mobileNumber: {
          name: 'mobileNumber',
          title: 'Mobile Number',
          type: FormFieldType.phoneNumber,
          value: defaultValues.mobileNumber,
          disabled: !defaultValues.newPerson,
          required: true
        }
      }
    },
    {
      sectionTitle: 'Business Partner Details',
      inputs: {
        relatedCompany: {
          name: 'relatedCompany',
          title: 'Related Company',
          placeholder: 'Related Company',
          type: FormFieldType.select,
          selectOptions: lovs.relatedCompanies,
          value: defaultValues.relatedCompany,
          required: false,
          hidden: true
        },
        bpType: {
          name: 'bpType',
          title: 'Type',
          placeholder: 'Type',
          type: FormFieldType.select,
          selectOptions: lovs.businessPartnerTypes,
          value: defaultValues.bpType,
          required: true
        },
        isCompany: {
          name: 'isCompany',
          title: 'Is Company',
          type: FormFieldType.switch,
          value: defaultValues.isCompany,
          required: true
        },
        isLocal: {
          name: 'isLocal',
          title: 'Is Local',
          type: FormFieldType.switch,
          value: defaultValues.isLocal,
          required: true
        },
        isRegistered: {
          name: 'isRegistered',
          title: 'Is Registered',
          type: FormFieldType.switch,
          value: defaultValues.isRegistered,
          required: true
        },
        registerNumber: {
          name: 'registerNumber',
          title: 'Registration Number',
          placeholder: 'Registration Number',
          type: FormFieldType.text,
          value: defaultValues.registerNumber
        },
        registrationExpiryDate: {
          name: 'registrationExpiryDate',
          title: 'Registration Expiry Date',
          type: FormFieldType.date,
          value: defaultValues.registrationExpiryDate
        }
      }
    },
    {
      sectionTitle: 'Primary Address Details',
      hidden: false,
      inputs: {
        addressType: {
          name: 'addressType',
          title: 'Address Type',
          placeholder: 'Select Address Type',
          type: FormFieldType.select,
          selectOptions: lovs.addressTypes,
          value: defaultValues.addressType,
          required: true
        },
        country: {
          name: 'country',
          title: 'Country',
          placeholder: 'Country',
          type: FormFieldType.select,
          selectOptions: lovs.countries,
          value: defaultValues.country,
          required: true
        },
        zone: {
          name: 'zone',
          title: 'Zone',
          placeholder: 'Zone',
          type: FormFieldType.select,
          selectOptions:
            defaultValues.country === 'LB' && lovs['zones']
              ? lovs['zones']
              : {},
          value: defaultValues.zone
        },
        city: {
          name: 'city',
          title: 'City',
          placeholder: 'City',
          type: FormFieldType.text,
          value: defaultValues.city,
          required: true
        },
        street: {
          name: 'street',
          title: 'Street',
          placeholder: 'Street',
          type: FormFieldType.text,
          value: defaultValues.street
        },
        building: {
          name: 'building',
          title: 'Building',
          placeholder: 'Building',
          type: FormFieldType.text,
          value: defaultValues.building
        },
        addressDetails: {
          name: 'addressDetails',
          title: 'Address Details',
          placeholder: 'Address Details',
          type: FormFieldType.text,
          value: defaultValues.addressDetails
        },
        addressDetailsArabic: {
          name: 'addressDetailsArabic',
          title: 'Address Details (Arabic)',
          placeholder: 'Address Details In Arabic',
          type: FormFieldType.text,
          value: defaultValues.addressDetailsArabic
        }
      }
    },
    {
      sectionTitle: 'Accounting Details',
      hidden: true,
      inputs: {
        taxAuxiliary: {
          name: 'taxAuxiliary',
          title: 'Tax Auxiliary',
          placeholder: 'Tax Auxiliary',
          type: FormFieldType.text,
          value: defaultValues.taxAuxiliary,
          required: false,
          customValidator: ({ taxAuxiliary }) => {
            return taxAuxiliary.length != 8
              ? 'Tax Auxiliary should be 8 characters'
              : '';
          }
        },
        auxiliary: {
          name: 'auxiliary',
          title: 'Auxiliary',
          placeholder: 'Auxiliary',
          type: FormFieldType.text,
          value: defaultValues.auxiliary,
          required: false,
          customValidator: ({ auxiliary }) => {
            return auxiliary.length != 8
              ? 'Auxiliary should be 8 characters'
              : '';
          }
        }
      }
    },
    {
      sectionTitle: 'Credit Limit Details',
      hidden: true,
      inputs: {
        maxCreditLimitAllowedCurrency: {
          name: 'maxCreditLimitAllowedCurrency',
          title: 'Max Credit Limit Allowed (Currency)',
          placeholder: 'Currency',
          type: FormFieldType.select,
          selectOptions: lovs.currencies,
          value: defaultValues.maxCreditLimitAllowedCurrency,
          required: false
        },
        maxCreditLimitAllowed: {
          name: 'maxCreditLimitAllowed',
          title: 'Max Credit Limit Allowed',
          placeholder: 'Max Credit Limit Allowed',
          type: FormFieldType.currency,
          maxDecimalPrecision: 0,
          value: defaultValues.maxCreditLimitAllowed,
          required: false
        }
      }
    },
    {
      sectionTitle: 'Additional Details',
      hidden: true,
      inputs: {
        allowAccessToAllInsured: {
          name: 'allowAccessToAllInsured',
          title: 'Allow Access to All Insureds',
          type: FormFieldType.switch,
          value: defaultValues.allowAccessToAllInsured,
          required: false
        }
      }
    }
  ];
};

export const exportBusinessPartnersDrawerSections = (
  values: IExportBusinessPartnersFilters,
  lovs: Record<string, Record<string, string>>
): IDynamicFormWidgetSection[] => {
  return [
    {
      inputs: {
        businessPartnerType: {
          name: 'businessPartnerType',
          title: 'Type',
          type: FormFieldType.multiSelect,
          placeholder: 'Business Partner Type',
          selectOptions: lovs['BusinessPartnerTypes'],
          value: values?.businessPartnerType ?? [ALL],
          required: true
        },
        businessPartnerStatus: {
          name: 'businessPartnerStatus',
          title: 'Status',
          type: FormFieldType.multiSelect,
          placeholder: 'Status',
          selectOptions: lovs['BusinessPartnerStatuses'],
          value: values?.businessPartnerStatus ?? [ALL],
          required: true,
          hidden: false
        },
        businessPartnerCreatedOn: {
          name: 'businessPartnerCreatedOn',
          title: 'Created On',
          type: FormFieldType.dateRange,
          placeholder: 'Select Date Range',
          value: values?.businessPartnerCreatedOn ?? [],
          required: false
        }
      }
    }
  ];
};

export const exportBusinessPartnersExcelHeaders = {
  code: { label: 'Code', visible: true, isDate: false, isCurrency: false },
  fullName: {
    label: 'Full Name',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  type: { label: 'Type', visible: true, isDate: false, isCurrency: false },
  isCompany: {
    label: 'IsCompany',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  isLocal: {
    label: 'IsLocal',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  isRegistered: {
    label: 'IsRegistered',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  registrationNumber: {
    label: 'Registration Number',
    visible: true,
    isDate: false,
    isCurrency: false
  },
  registrationExpiryDate: {
    label: 'Registration Expiry Date',
    visible: true,
    isDate: true,
    isCurrency: false
  },
  status: { label: 'Status', visible: true, isDate: false, isCurrency: false }
};

export const exportBusinessPartnersExcelColumnWidths = [
  15, // Code
  25, // Full Name
  15, // Type
  12, // IsCompany
  12, // IsLocal
  15, // IsRegistered
  20, // Registration Number
  20, // Registration Expiry Date
  15 // Status
];

export const exportBusinessPartnersExcelTemplateStyles = {
  header: {
    font: { sz: 11, bold: true, color: { rgb: 'FFFFFFFF' } },
    fill: { patternType: 'solid', fgColor: { rgb: 'FF1F4E79' } },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true }
  },
  cell: {
    alignment: { vertical: 'center', wrapText: true }
  }
};

export const modifyBusinessPartnerDrawerSections = (
  defaultValues: IAbstractRecord,
  lovs: Record<string, Record<string, string>>
): IDynamicFormWidgetSection[] => {
  const isIndividual = defaultValues?.type === 'INDIVIDUAL';
  const isCompany = defaultValues?.type === 'COMPANY';

  return [
    {
      sectionTitle: 'Person Details',
      inputs: {
        title: {
          name: 'title',
          title: 'Title',
          type: FormFieldType.select,
          selectOptions: lovs.personTitles,
          value: defaultValues?.title,
          hidden: !isIndividual,
          required: isIndividual,
          disabled: true
        },
        firstName: {
          name: 'firstName',
          title: 'First Name',
          type: FormFieldType.text,
          value: defaultValues?.firstName,
          hidden: !isIndividual,
          required: isIndividual,
          disabled: true
        },
        middleName: {
          name: 'middleName',
          title: 'Middle Name',
          type: FormFieldType.text,
          value: defaultValues?.middleName,
          hidden: !isIndividual,
          required: false,
          disabled: true
        },
        lastName: {
          name: 'lastName',
          title: 'Last Name',
          type: FormFieldType.text,
          value: defaultValues?.lastName,
          hidden: !isIndividual,
          required: isIndividual,
          disabled: true
        },
        dateOfBirth: {
          name: 'dateOfBirth',
          title: 'Date Of Birth',
          type: FormFieldType.date,
          value: defaultValues?.dateOfBirth,
          maxDate: new Date(),
          hidden: !isIndividual,
          required: isIndividual,
          disabled: true
        },
        companyName: {
          name: 'companyName',
          title: 'Company Name',
          type: FormFieldType.text,
          value: defaultValues?.companyName,
          hidden: !isCompany,
          required: isCompany,
          disabled: true
        },
        email: {
          name: 'email',
          title: 'Email',
          type: FormFieldType.text,
          value: defaultValues?.email,
          required: true,
          customValidator: ({ email }) =>
            email && !REGEX.email.test(email) ? 'Invalid Email' : '',
          disabled: true
        },
        mobileNumber: {
          name: 'mobileNumber',
          title: 'Mobile Number',
          type: FormFieldType.phoneNumber,
          value: defaultValues?.mobileNumber,
          required: true,
          disabled: true
        }
      }
    },
    {
      sectionTitle: 'Business Partner Details',
      inputs: {
        relatedCompany: {
          name: 'relatedCompany',
          title: 'Related Company',
          type: FormFieldType.select,
          selectOptions: lovs.relatedCompanies,
          value: defaultValues?.relatedCompany,
          required: false,
          hidden: true
        },
        bpType: {
          name: 'bpType',
          title: 'Type',
          type: FormFieldType.select,
          selectOptions: lovs.businessPartnerTypes,
          value: defaultValues?.bpType,
          required: true
        },
        isCompany: {
          name: 'isCompany',
          title: 'Is Company',
          type: FormFieldType.switch,
          value: defaultValues?.isCompany,
          required: true
        },
        isLocal: {
          name: 'isLocal',
          title: 'Is Local',
          type: FormFieldType.switch,
          value: defaultValues?.isLocal,
          required: true
        },
        isRegistered: {
          name: 'isRegistered',
          title: 'Is Registered',
          type: FormFieldType.switch,
          value: defaultValues?.isRegistered,
          required: true
        },
        registerNumber: {
          name: 'registerNumber',
          title: 'Registration Number',
          placeholder: 'Registration Number',
          type: FormFieldType.text,
          value: defaultValues?.registerNumber
        },
        registrationExpiryDate: {
          name: 'registrationExpiryDate',
          title: 'Registration Expiry Date',
          type: FormFieldType.date,
          value: defaultValues?.registrationExpiryDate
        }
      }
    },
    {
      sectionTitle: 'Accounting Details',
      hidden: true,
      inputs: {
        taxAuxiliary: {
          name: 'taxAuxiliary',
          title: 'Tax Auxiliary',
          type: FormFieldType.text,
          value: defaultValues?.taxAuxiliary,
          required: false,
          customValidator: ({ taxAuxiliary }) =>
            taxAuxiliary?.length !== 8
              ? 'Tax Auxiliary should have 8 characters'
              : ''
        },
        auxiliary: {
          name: 'auxiliary',
          title: 'Auxiliary',
          type: FormFieldType.text,
          value: defaultValues?.auxiliary,
          required: false,
          customValidator: ({ auxiliary }) =>
            auxiliary?.length !== 8 ? 'Auxiliary should have 8 characters' : ''
        }
      }
    },
    {
      sectionTitle: 'Additional Details',
      hidden: true,
      inputs: {
        allowAccessToAllInsured: {
          name: 'allowAccessToAllInsured',
          title: 'Allow Access to All Insureds',
          type: FormFieldType.switch,
          value: defaultValues?.allowAccessToAllInsured,
          required: false
        }
      }
    }
  ];
};

export const modifyAddressDrawerSections = (
  defaultValues: IAbstractRecord,
  lovs: Record<string, Record<string, string>>,
  countryCode?: string
): IDynamicFormWidgetSection[] => {
  console.log('[Sections] defaultValues:', defaultValues);
  console.log('[Sections] countryCode:', countryCode);

  return [
    {
      sectionTitle: 'Address Information',
      inputs: {
        type: {
          name: 'type',
          title: 'Address Type',
          placeholder: 'Address Type',
          type: FormFieldType.select,
          selectOptions: lovs.addressTypes,
          value: defaultValues.type,
          required: true
        },

        country: {
          name: 'country',
          title: 'Country',
          placeholder: 'Choose Country',
          type: FormFieldType.select,
          selectOptions: lovs.countries,
          value: defaultValues.country,
          required: true
        },

        zone: {
          name: 'zone',
          title: 'Zone',
          placeholder: 'Choose Zone',
          type: FormFieldType.select,

          selectOptions: countryCode === 'LB' && lovs.zones ? lovs.zones : {},

          value: defaultValues.zone,

          hidden: defaultValues.country !== 'LB'
        },

        city: {
          name: 'city',
          title: 'City',
          placeholder: 'City',
          type: FormFieldType.text,
          value: defaultValues.city,
          required: true
        },

        street: {
          name: 'street',
          title: 'Street',
          placeholder: 'Street',
          type: FormFieldType.text,
          value: defaultValues.street
        },

        building: {
          name: 'building',
          title: 'Building',
          placeholder: 'Building',
          type: FormFieldType.text,
          value: defaultValues.building
        },

        addressDetails: {
          name: 'addressDetails',
          title: 'Address Details',
          placeholder: 'Address Details',
          type: FormFieldType.text,
          value: defaultValues.addressDetails
        },

        addressDetailsArabic: {
          name: 'addressDetailsArabic',
          title: 'Address Details Arabic',
          placeholder: 'Address Details Arabic',
          type: FormFieldType.text,
          value: defaultValues.addressDetailsArabic
        }
      }
    }
  ];
};

export const modifyTaxationDrawerSections = (
  initial: ModifyTaxationDrawerFormValues,
  lineOption: Record<string, string>,
  currencyOptions: Record<string, string>
): IDynamicFormWidgetSection[] => [
  {
    inputs: {
      lineID: {
        name: 'lineID',
        title: 'Line',
        type: FormFieldType.select,
        placeholder: 'Line',
        value: initial.lineID,
        required: true,
        selectOptions: lineOption,
        disabled: true
      },
      effectiveFrom: {
        name: 'effectiveFrom',
        title: 'Effective From',
        placeholder: 'Effective From',
        type: FormFieldType.date,
        disabled: true,
        value: initial.effectiveFrom,
        required: true
      },
      effectiveTo: {
        name: 'effectiveTo',
        title: 'Effective To',
        placeholder: 'Effective To',
        type: FormFieldType.date,
        value: initial.effectiveTo,
        required: false,
        disabled: true
      },
      fixedStamp: {
        name: 'fixedStamp',
        title: 'Fixed Stamp',
        placeholder: 'Fixed Stamp',
        type: FormFieldType.currency,
        value: initial.fixedStamp,
        required: true
      },
      fixedStampCurrency: {
        name: 'fixedStampCurrency',
        title: 'Fixed Stamp Currency',
        type: FormFieldType.select,
        placeholder: 'Fixed Stamp Currency',
        value: initial.fixedStampCurrency,
        required: true,
        selectOptions: currencyOptions
      },
      proportionalStamp: {
        name: 'proportionalStamp',
        title: 'Proportional Stamp',
        placeholder: 'Proportional Stamp',
        type: FormFieldType.percentage,
        value: initial.proportionalStamp,
        required: true
      },
      municipalityTax: {
        name: 'municipalityTax',
        title: 'Municipality Tax',
        placeholder: 'Municipality Tax',
        type: FormFieldType.percentage,
        value: initial.municipalityTax,
        required: true
      },
      taxOnCommission: {
        name: 'taxOnCommission',
        title: 'Tax On Commission',
        placeholder: 'Tax On Commission',
        type: FormFieldType.percentage,
        value: initial.taxOnCommission,
        required: true
      },
      reinsuranceTax: {
        name: 'reinsuranceTax',
        title: 'Reinsurance Tax',
        placeholder: 'Reinsurance Tax',
        type: FormFieldType.percentage,
        value: initial.reinsuranceTax,
        required: true
      }
    }
  }
];

export const newPlanSpecificTaxationDrawerSections = (
  initial: NewPlanSpecificTaxationFormValues,
  lineOptions: Record<string, string>,
  planOptions: Record<string, string>,
  currencyOptions: Record<string, string>,
  disableLine = true
): IDynamicFormWidgetSection[] => [
  {
    inputs: {
      lineID: {
        name: 'lineID',
        title: 'Line',
        type: FormFieldType.select,
        placeholder: 'Select Line',
        value: initial.lineID,
        required: true,
        selectOptions: lineOptions,
        disabled: disableLine
      },
      planID: {
        name: 'planID',
        title: 'Plan',
        type: FormFieldType.select,
        placeholder: 'Select Plan',
        value: initial.planID,
        required: true,
        selectOptions: planOptions,
        disabled: false
      },
      fixedStamp: {
        name: 'fixedStamp',
        title: 'Fixed Stamp',
        placeholder: 'Fixed Stamp',
        type: FormFieldType.currency,
        value: initial.fixedStamp,
        required: true
      },
      fixedStampCurrency: {
        name: 'fixedStampCurrency',
        title: 'Fixed Stamp Currency',
        type: FormFieldType.select,
        placeholder: 'Select Currency',
        value: initial.fixedStampCurrency,
        required: true,
        selectOptions: currencyOptions
      },
      proportionalStamp: {
        name: 'proportionalStamp',
        title: 'Proportional Stamp',
        placeholder: 'Proportional Stamp',
        type: FormFieldType.percentage,
        value: initial.proportionalStamp,
        required: true
      },
      municipalityTax: {
        name: 'municipalityTax',
        title: 'Municipality Tax',
        placeholder: 'Municipality Tax',
        type: FormFieldType.percentage,
        value: initial.municipalityTax,
        required: true
      },
      taxOnCommission: {
        name: 'taxOnCommission',
        title: 'Tax On Commission',
        placeholder: 'Tax On Commission',
        type: FormFieldType.percentage,
        value: initial.taxOnCommission,
        required: true
      },
      reinsuranceTax: {
        name: 'reinsuranceTax',
        title: 'Reinsurance Tax',
        placeholder: 'Reinsurance Tax',
        type: FormFieldType.percentage,
        value: initial.reinsuranceTax,
        required: true
      }
    }
  }
];

export const modifyPlanSpecificTaxationDrawerSections = (
  initial: ModifyPlanSpecificTaxationFormValues,
  lineOptions: Record<string, string>,
  planOptions: Record<string, string>,
  currencyOptions: Record<string, string>
): IDynamicFormWidgetSection[] => [
  {
    inputs: {
      lineID: {
        name: 'lineID',
        title: 'Line',
        type: FormFieldType.select,
        placeholder: 'Select Line',
        value: initial.lineID,
        required: true,
        selectOptions: lineOptions,
        disabled: true
      },
      planID: {
        name: 'planID',
        title: 'Plan',
        type: FormFieldType.select,
        placeholder: 'Select Plan',
        value: initial.planID,
        required: true,
        selectOptions: planOptions,
        disabled: true
      },
      fixedStamp: {
        name: 'fixedStamp',
        title: 'Fixed Stamp',
        placeholder: 'Fixed Stamp',
        type: FormFieldType.currency,
        value: initial.fixedStamp,
        required: true
      },
      fixedStampCurrency: {
        name: 'fixedStampCurrency',
        title: 'Fixed Stamp Currency',
        type: FormFieldType.select,
        placeholder: 'Select Currency',
        value: initial.fixedStampCurrency,
        required: true,
        selectOptions: currencyOptions
      },
      proportionalStamp: {
        name: 'proportionalStamp',
        title: 'Proportional Stamp',
        placeholder: 'Proportional Stamp',
        type: FormFieldType.percentage,
        value: initial.proportionalStamp,
        required: true
      },
      municipalityTax: {
        name: 'municipalityTax',
        title: 'Municipality Tax',
        placeholder: 'Municipality Tax',
        type: FormFieldType.percentage,
        value: initial.municipalityTax,
        required: true
      },
      taxOnCommission: {
        name: 'taxOnCommission',
        title: 'Tax On Commission',
        placeholder: 'Tax On Commission',
        type: FormFieldType.percentage,
        value: initial.taxOnCommission,
        required: true
      },
      reinsuranceTax: {
        name: 'reinsuranceTax',
        title: 'Reinsurance Tax',
        placeholder: 'Reinsurance Tax',
        type: FormFieldType.percentage,
        value: initial.reinsuranceTax,
        required: true
      }
    }
  }
];

export const defaultCommissionDrawerSections = (
  values: IDefaultCommissionInfo,
  lovs: IDefaultCommissionLovs
): IDynamicFormWidgetSection[] => [
  {
    inputs: {
      assignedLine: {
        name: 'assignedLineId',
        title: 'Line',
        type: FormFieldType.text,
        placeholder: 'Line',
        value: values.assignedLineName,
        required: true,
        disabled: true
      },
      commissionType: {
        name: 'commissionType',
        title: 'Commission Type',
        placeholder: 'Commission Type',
        type: FormFieldType.select,
        value: values.commissionType ?? 'RATE',
        required: true,
        selectOptions: lovs.commissionType
      },
      valueCurrency: {
        name: 'valueCurrency',
        title: 'Commission Currency',
        placeholder: 'Commission Currency',
        type: FormFieldType.select,
        value: values.valueCurrency,
        selectOptions: lovs.valueCurrency ?? {},
        conditionalRequired: (values) => values.commissionType === 'AMOUNT',
        conditionalDisable: (values) => values.commissionType !== 'AMOUNT',
        conditionalHidden: (values) => values.commissionType !== 'AMOUNT'
      },
      lineCommissionValue: {
        name: 'lineCommissionValue',
        title: 'Commission',
        placeholder: 'Commission',
        type: FormFieldType.currency,
        value: values.lineCommissionValue ?? '',
        conditionalRequired: (values) => values.commissionType === 'AMOUNT',
        conditionalDisable: (values) => values.commissionType !== 'AMOUNT',
        conditionalHidden: (values) => values.commissionType !== 'AMOUNT'
      },

      lineCommissionRate: {
        name: 'lineCommissionRate',
        title: 'Commission Rate',
        type: FormFieldType.percentage,
        placeholder: 'Commission Rate',
        value:
          values.lineCommissionRate === null ||
          values.lineCommissionRate === undefined
            ? ''
            : trimTrailingZeros(values.lineCommissionRate),
        conditionalRequired: (v) => v.commissionType !== 'AMOUNT',
        conditionalDisable: (v) => v.commissionType === 'AMOUNT',
        conditionalHidden: (v) => v.commissionType === 'AMOUNT',
        customValidator: (values) => {
          if (
            values.lineCommissionRate &&
            Number(values.lineCommissionRate) > 65
          ) {
            return 'Commission Rate cannot be greater than 65%';
          }

          return '';
        }
      },

      applicableOn: {
        name: 'applicableOn',
        title: 'Applicable On',
        placeholder: 'Commission Applicable On',
        type: FormFieldType.select,
        value: Object.keys(lovs.applicableOn).find(
          (key) => lovs.applicableOn[key] === 'Total Premium'
        ),
        required: false,
        hidden: true,
        selectOptions: lovs.applicableOn
      }
    }
  }
];

export const assignPlansDrawerSections = (
  values: Record<string, any> = initialValues,
  lovs: Record<string, Record<string, string>>,
  isDirectSalesforce: boolean,
  dateConstraints?: { effectiveFrom?: string; effectiveTo?: string }
): IDynamicFormWidgetSection[] => {
  const isRate = values.commissionType?.toLowerCase() === 'rate';

  return [
    {
      inputs: {
        line: {
          name: 'line',
          title: 'Line',
          type: FormFieldType.select,
          placeholder: 'Select Line',
          value: !isEmpty(values.line) ? values.line : '',
          required: true,
          selectOptions: lovs.line || {}
        },
        plan: {
          name: 'plan',
          title: 'Plan(s)',
          type: FormFieldType.multiSelect,
          placeholder: 'Select Plan(s)',
          value: !isEmpty(values.plan) ? values.plan : '',
          required: true,
          selectOptions: lovs.plan || {}
        },
        commissionType: {
          name: 'commissionType',
          title: 'Commission Type',
          placeholder: 'Commission Type',
          required: true,
          type: FormFieldType.select,
          value: values.commissionType,
          selectOptions: lovs.commissionType || {}
        },
        lineCommissionRate: {
          name: 'lineCommissionRate',
          title: 'Commission Rate',
          type: FormFieldType.percentage,
          placeholder: 'Commission Rate',
          value: values.lineCommissionRate,
          required: isRate,
          hidden: !isRate || isDirectSalesforce,
          disabled: !isRate,
          customValidator: (values) => {
            if (
              values.lineCommissionRate &&
              Number(values.lineCommissionRate) > 65
            ) {
              return 'Commission Rate cannot be greater than 65%';
            }

            return '';
          }
        },
        ApplicableOn: {
          name: 'ApplicableOn',
          title: 'Applicable On',
          placeholder: 'Applicable On',
          type: FormFieldType.select,
          value: Object.keys(lovs.ApplicableOn).find(
            (key) => lovs.ApplicableOn[key] === 'Total Premium'
          ),
          required: false,
          hidden: true,
          selectOptions: lovs.ApplicableOn || {}
        },
        ValueCurrency: {
          name: 'ValueCurrency',
          title: 'Commission Currency',
          placeholder: 'Currency',
          type: FormFieldType.select,
          value: !isEmpty(values.ValueCurrency) ? values.ValueCurrency : '',
          required: !isRate,
          hidden: isRate || isDirectSalesforce,
          selectOptions: lovs.ValueCurrency || {}
        },
        lineCommissionValue: {
          name: 'lineCommissionValue',
          title: 'Commission',
          type: FormFieldType.currency,
          placeholder: 'Commission',
          value: values.lineCommissionValue,
          required: !isRate,
          hidden: isRate || isDirectSalesforce,
          disabled: isRate
        },
        activeFrom: {
          name: 'activeFrom',
          title: 'Active From',
          placeholder: 'Active From',
          type: FormFieldType.date,
          value: values.activeFrom ? new Date(values.activeFrom) : '',
          minDate:
            Array.isArray(values.plan) &&
            values.plan.length === 1 &&
            dateConstraints?.effectiveFrom
              ? new Date(dateConstraints.effectiveFrom)
              : undefined,
          maxDate: values.activeTo
            ? dayjs(values.activeTo).subtract(1, 'day').toDate()
            : Array.isArray(values.plan) &&
                values.plan.length === 1 &&
                dateConstraints?.effectiveTo
              ? dayjs(dateConstraints.effectiveTo).subtract(1, 'day').toDate()
              : undefined,

          required: true,
          customValidator: (values) => {
            const { activeFrom, activeTo } = values;
            if (
              activeFrom &&
              activeTo &&
              DateService.isDateBefore(activeTo, activeFrom)
            ) {
              return 'Active From should be less than Active To';
            }

            return '';
          }
        },
        activeTo: {
          name: 'activeTo',
          title: 'Active To',
          placeholder: 'Active To',
          type: FormFieldType.date,
          value: values.activeTo ? new Date(values.activeTo) : '',
          minDate: values.activeFrom
            ? dayjs(values.activeFrom).add(1, 'day').toDate()
            : Array.isArray(values.plan) &&
                values.plan.length === 1 &&
                dateConstraints?.effectiveFrom
              ? dayjs(dateConstraints.effectiveFrom).add(1, 'day').toDate()
              : undefined,
          maxDate:
            Array.isArray(values.plan) &&
            values.plan.length === 1 &&
            dateConstraints?.effectiveTo
              ? new Date(dateConstraints.effectiveTo)
              : undefined,
          required: true,
          customValidator: (values) => {
            const { activeFrom, activeTo } = values;
            if (
              activeFrom &&
              activeTo &&
              DateService.isDateBefore(activeTo, activeFrom)
            ) {
              return 'Active To should be greater than Active From';
            }

            return '';
          }
        }
      }
    }
  ];
};

export const setPlansCommissionInitialValues = {
  selectedPlans: [''],
  commissionType: '',
  lineCommissionValue: '',
  lineCommissionValueCurrency: '',
  lineCommissionRate: '',
  lineCommissionApplicableOn: '',
  activeFrom: '',
  activeTo: ''
};

export const setPlansCommissionDrawerSections: IDynamicFormWidgetSection[] = [
  {
    inputs: {
      selectedPlans: {
        name: 'selectedPlans',
        title: 'Plan(s)',
        placeholder: 'Plans',
        type: FormFieldType.multiSelect,
        value: setPlansCommissionInitialValues.selectedPlans,
        required: true,
        disabled: true,
        selectOptions: {}
      },
      commissionType: {
        name: 'commissionType',
        title: 'Commission Type',
        placeholder: 'Commission Type',
        type: FormFieldType.select,
        value: !isEmpty(setPlansCommissionInitialValues.commissionType)
          ? setPlansCommissionInitialValues.commissionType
          : '',
        required: true,
        selectOptions: {}
      },
      lineCommissionRate: {
        name: 'lineCommissionRate',
        title: 'Commission Rate',
        type: FormFieldType.percentage,
        placeholder: 'Commission Rate',
        value: setPlansCommissionInitialValues.lineCommissionRate,
        conditionalHidden: (values) => {
          const isRate = values?.commissionType?.toLowerCase() === 'rate';

          return !isRate;
        },
        conditionalDisable: (values) => {
          const isRate = values?.commissionType?.toLowerCase() === 'rate';

          return !isRate;
        },
        conditionalRequired: (values) => {
          const isRate = values?.commissionType?.toLowerCase() === 'rate';

          return isRate;
        },
        customValidator: (values) => {
          if (
            values.lineCommissionRate &&
            Number(values.lineCommissionRate) > 65
          ) {
            return 'Commission Rate cannot be greater than 65%';
          }

          return '';
        }
      },
      lineCommissionApplicableOn: {
        name: 'lineCommissionApplicableOn',
        title: 'Applicable On',
        placeholder: 'Commission Applicable On',
        type: FormFieldType.select,
        value: !isEmpty(
          setPlansCommissionInitialValues.lineCommissionApplicableOn
        )
          ? setPlansCommissionInitialValues.lineCommissionApplicableOn
          : '',
        selectOptions: {},
        hidden: true
      },
      lineCommissionValueCurrency: {
        name: 'lineCommissionValueCurrency',
        title: 'Commission Currency',
        placeholder: 'Currency',
        type: FormFieldType.select,
        value: !isEmpty(
          setPlansCommissionInitialValues.lineCommissionValueCurrency
        )
          ? setPlansCommissionInitialValues.lineCommissionValueCurrency
          : '',
        selectOptions: {},
        conditionalHidden: (values) => {
          const isRate = values?.commissionType?.toLowerCase() === 'rate';

          return isRate;
        },
        conditionalDisable: (values) => {
          const isRate = values?.commissionType?.toLowerCase() === 'rate';

          return isRate;
        },
        conditionalRequired: (values) => {
          const isRate = values?.commissionType?.toLowerCase() === 'rate';

          return !isRate;
        }
      },
      lineCommissionValue: {
        name: 'lineCommissionValue',
        title: 'Commission',
        type: FormFieldType.currency,
        placeholder: 'Commission Amout',
        value: setPlansCommissionInitialValues.lineCommissionValue,
        conditionalHidden: (values) => {
          const isRate = values?.commissionType?.toLowerCase() === 'rate';

          return isRate;
        },
        conditionalDisable: (values) => {
          const isRate = values?.commissionType?.toLowerCase() === 'rate';

          return isRate;
        },
        conditionalRequired: (values) => {
          const isRate = values?.commissionType?.toLowerCase() === 'rate';

          return !isRate;
        }
      },
      activeFrom: {
        name: 'activeFrom',
        title: 'Active From',
        placeholder: 'Active From',
        type: FormFieldType.date,
        value: setPlansCommissionInitialValues.activeFrom,
        required: true,
        customValidator: (values) => {
          const { activeTo, activeFrom } = values;
          if (
            activeTo &&
            activeFrom &&
            DateService.isDateBefore(activeTo, activeFrom)
          ) {
            return 'Effective To Date shall be equal or greater than Effective From Date';
          }

          return '';
        }
      },
      activeTo: {
        name: 'activeTo',
        title: 'Active To',
        placeholder: 'Active To',
        type: FormFieldType.date,
        value: setPlansCommissionInitialValues.activeTo,
        required: true
      }
    }
  }
];
